import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { AppRouter } from '@/api/_trpc/router'
import { assertNever } from '@/lib/assert-never'
import { sanitizeBrowseFilters } from '@/lib/coaching/browse-filters'
import type { ContainerRef } from '@/lib/coaching/curriculum-query'
import {
  type CoachingPace,
  derivedEstimate,
  effectiveEstimateMinutes,
} from '@/lib/coaching/estimate-minutes'
import { LEARNING_ITEM_COMPLETION_MODE } from '@/lib/coaching/learning-item-projection'
import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'
import { trpc } from '@/lib/trpc'
import {
  errorCode,
  errorReason,
  isRetryableFailure,
} from '../shared/trpc-error'
import { CoachingCurriculumProvider } from '../store'
import {
  type CascadeDestination,
  type ScheduleDay,
  type TrayCard,
  toCompletionMode,
  unpublishBlockedReason,
  type Week,
  type WeekSprintContext,
  type WeekSprintGap,
} from './_lib/auto-place'
import { formatMinutesDuration } from './_lib/duration'
import type { LibraryItem } from './_lib/filter-engine'
import {
  nextSeenFilter,
  readStoredSeenFilter,
  type SeenFilter,
  writeStoredSeenFilter,
} from './_lib/seen-filter'
import type { SortSpec } from './_lib/sorting'
import {
  foldedWeek,
  readStoredWeekFolds,
  type WeekFoldOverrides,
  withWeekFoldToggled,
  writeStoredWeekFolds,
} from './_lib/week-fold'
import {
  type CardDetailsResult,
  type CardDetailsSaveResult,
  type CardReviewState,
  type CompanyTag,
  type CurriculumSlice,
  type CustomCardInput,
  DEFAULT_TYPE_FILTER,
  type LibraryTab,
  type SourceFilterOption,
  type TypeFilter,
} from './_state/contracts'
import {
  type BrowseCorpusState,
  type BrowseFiltersInput,
  type BrowsePageFetcher,
  normalizeBrowseFilters,
  useBrowseCorpus,
} from './use-browse-corpus'

// Re-exported so the many modules that already reach for the browse filter
// slice through this hook keep a single import site.
export type { BrowseFiltersInput }

type RouterInputs = inferRouterInputs<AppRouter>
type RouterOutputs = inferRouterOutputs<AppRouter>
type Inputs = RouterInputs['admin']['coachingV2']['curriculum']
type Outputs = RouterOutputs['admin']['coachingV2']['curriculum']
// The details modal's review read is the one thing this hook fetches from
// outside the curriculum router: a review belongs to the review surface,
// which owns its own scoping rules, so the procedure lives there (LG-504)
// and only its call lands here.
type ReviewInputs = RouterInputs['admin']['coachingV2']['reviews']
type ReviewOutputs = RouterOutputs['admin']['coachingV2']['reviews']
// Exported so test fixtures can `satisfies Plan` themselves. They reach the
// hook through a deliberately loose api-override type, so without it a fixture
// that misspells a field compiles, and a test can then pass for a reason that
// has nothing to do with what it claims to check.
export type Plan = Outputs['getPlan']
type Browse = Outputs['browseLearningItems']
type Calendar = Outputs['calendar']
type SeenSummary = Outputs['seenSummary']

interface QueryState<T> {
  data: T | undefined
  isLoading: boolean
  error: unknown
  refetch: () => Promise<unknown>
}

type Command<Name extends keyof Inputs> = (
  input: Inputs[Name],
) => Promise<Outputs[Name]>

/**
 * Where a freshly-staged card lands. `'auto'` picks the first day with room
 * (the library's own "add" affordance); `{ weekId, dayId }` is a day the
 * coach picked explicitly (the related-problem modal's "Assign in addition"
 * — LG-360 §8, Task 11, and the day column's own "+ custom card").
 *
 * Identity, not position, and the distinction is the whole point. The modal
 * and the day column both pick by `{ weekIdx, dayIdx }`, but a stage that
 * fails retryably stores a retry the coach clicks *after* the catch's
 * `refreshPlan()` — and that refresh calls `invalidatePlanCaches`, which
 * drops every paged-in range. `weeks` is sorted by `startsOn`, so "Load
 * earlier" prepends and dropping those ranges shortens the array *from the
 * front*: every index shifts. A retry holding an index would silently
 * retarget itself at whatever week now sits at that position, and because
 * it also reads that week's current version the server accepts the
 * misplacement instead of rejecting it (LG-457). Callers resolve index → id
 * once, at the moment the coach acts; a retry re-finds by id and takes the
 * `pickedDayUnavailable` branch when the id is not on the board.
 *
 * Not on the board is broader than deleted, and deliberately so: the same
 * `invalidatePlanCaches` that shifts the indices also empties
 * `additionalPlanRanges`, and nothing re-pages it, so a day the coach reached
 * through "Load earlier" is unreachable to the retry even though the server
 * still has it. Declining to place is the right call either way — the board
 * cannot confirm the week's version, so a placement would be guesswork — but
 * it means this branch reports "no longer available", not "deleted", and the
 * coach's next step is to reopen the range rather than to mourn the day.
 *
 * `'picked-day-gone'` is its own member rather than nullable ids inside the
 * object, because "the coach's pick still resolves" is one all-or-nothing
 * fact: a `{ weekId: string | null; dayId: string | null }` can represent
 * three states no producer can build, and re-establishing the nullability at
 * each producer is a `??` away from shipping `undefined` into a field typed
 * `string | null`. An index that already resolves to nothing on the *first*
 * attempt is still an explicit pick and must be reported, not quietly
 * downgraded to a tray add, so it maps to this member and lands in
 * `pickedDayUnavailable`.
 */
type StageDestination =
  | 'tray'
  | 'auto'
  | 'picked-day-gone'
  | { weekId: string; dayId: string }

/**
 * Whether the staged card ended up on a day, and — the part that matters for
 * what the coach is told — whether *this run* is what put it there.
 *
 * `'already'` is the creation-key replay: the assignment came back with a
 * `planDayId` this call never wrote, set either by the original attempt whose
 * response was lost or by the coach dragging the tray card somewhere before
 * pressing Retry. The day it is on is then a fact this call does not know, so
 * no sentence may name one.
 */
type StagePlacement = 'none' | 'this-run' | 'already'

/**
 * What the coach is told after a stage that did not throw.
 *
 * A function ending in `assertNever` rather than a ternary chain at the call
 * site, because a chain absorbs a new `StageDestination` member into whatever
 * arm happens to be last — which is exactly how `'picked-day-gone'` came to
 * claim `'auto'`'s "added using the first available day" for a search that
 * never ran. A new member must fail to compile until it is given a sentence.
 *
 * Reads the placement before the destination throughout: the destination is
 * where the card was *headed*, and the two diverge in precisely the replay
 * cases this board exists to survive. Every `'already'` arm is deliberately
 * neutral — naming the picked day would report an intent as an outcome, and
 * naming `'auto'`'s mechanism would credit a `firstFit` search that never ran.
 */
function stageOutcomeMessage(
  title: string,
  destination: StageDestination,
  chosenDay: ScheduleDay | null,
  placement: StagePlacement,
): string {
  if (placement === 'already') return `"${title}" was placed on the plan.`
  if (typeof destination === 'object') {
    if (placement === 'none')
      return `"${title}" was staged because no available day had room.`
    // Safe to name: `'this-run'` on an object destination means the placement
    // below resolved `chosenDay` and wrote it.
    return chosenDay === null
      ? `"${title}" was placed on the plan.`
      : `"${title}" was placed on ${chosenDay.label}.`
  }
  switch (destination) {
    case 'tray':
      return placement === 'none'
        ? `"${title}" added to the staging tray.`
        : `"${title}" was placed on the plan.`
    case 'auto':
      return placement === 'none'
        ? `"${title}" was staged because no available day had room.`
        : `"${title}" was added using the first available day.`
    case 'picked-day-gone':
      // Not `'auto'`'s capacity sentence: no `firstFit` search ran for this
      // member, so "no available day had room" would attribute the outcome to
      // a search that never happened. Unreachable today — the caller returns
      // early on `pickedDayUnavailable && placement === 'none'` with this same
      // sentence — but the arm has to stay truthful, because the whole point
      // of the exhaustive switch is that it survives someone narrowing that
      // guard later.
      return `"${title}" was added to the staging tray, but the day you picked is no longer available. Reopen it to place on a current day.`
    default:
      return assertNever(destination, 'Unknown stage destination')
  }
}

/**
 * What a cascade assign says it did (LG-907).
 *
 * Counts rather than a single verb, because a cascade has three independent
 * outcomes the coach acts on differently: how many cards were made, how many
 * of them fitted on a day, and which members already had an open card and so
 * were left alone. Collapsing those into "assigned" is the opaque
 * container-card the fan-out replaces, in sentence form.
 *
 * The already-open members are named, not counted: "2 were skipped" leaves the
 * coach to diff the chapter against the plan by hand to find out which two.
 */
/** How many already-open members the flash names before it starts counting. */
const ALREADY_ASSIGNED_NAME_LIMIT = 5

function cascadeOutcomeMessage(
  title: string,
  destination: CascadeDestination,
  outcome: {
    created: number
    placed: number
    /**
     * Cards this batch had already put on a day, handed back by a replay. No
     * `firstFit` search ran for them, so they are counted apart from `placed`
     * and never described as having found or missed room — the same
     * distinction `stageOutcomeMessage`'s `'already'` arm keeps, and for the
     * same reason: attributing an outcome to a search that never happened is
     * how a retry ends up telling the coach their cards are in the tray.
     */
    alreadyPlaced: number
    /**
     * Replayed cards past `in_progress` — finished or cancelled since the
     * attempt that made them. `place` refuses these, so no search ran for
     * them either, and folding them into the leftover would send the coach
     * looking in the tray for a card that is not there.
     */
    notPlaceable: number
    /** Whether a `firstFit` search actually came back empty. */
    noRoom: boolean
    alreadyAssigned: { title: string }[]
  },
): string {
  // Named rather than counted, but capped: re-assigning a chapter the client
  // already has is a normal click now that the container guard is gone, and
  // every member comes back — an uncapped join turns the flash into a wall of
  // titles nobody reads.
  const shown = outcome.alreadyAssigned.slice(0, ALREADY_ASSIGNED_NAME_LIMIT)
  const unshown = outcome.alreadyAssigned.length - shown.length
  const already =
    outcome.alreadyAssigned.length === 0
      ? ''
      : ` Already in this plan, so left alone: ${shown
          .map((member) => `"${member.title}"`)
          .join(', ')}${unshown === 0 ? '' : ` and ${unshown} more`}.`
  if (outcome.created === 0) {
    return outcome.alreadyAssigned.length === 0
      ? `"${title}" has nothing to assign.`
      : `Nothing new to add from "${title}".${already}`
  }
  const cards = outcome.created === 1 ? '1 card' : `${outcome.created} cards`
  switch (destination) {
    case 'tray':
      return `${cards} from "${title}" added to the staging tray.${already}`
    case 'auto': {
      if (outcome.placed === outcome.created) {
        return `${cards} from "${title}" placed on the first available days.${already}`
      }
      // Each clause states only what was observed, and every card the loop saw
      // lands in exactly one of them. The leftover is "staged" without a
      // reason unless a search really did fail, because a card can also be
      // left in the tray by a placement this loop never attempted.
      const remainder =
        outcome.created -
        outcome.placed -
        outcome.alreadyPlaced -
        outcome.notPlaceable
      const parts: string[] = []
      if (outcome.placed > 0) parts.push(`${outcome.placed} placed`)
      if (outcome.alreadyPlaced > 0) {
        parts.push(`${outcome.alreadyPlaced} already on the plan`)
      }
      if (outcome.notPlaceable > 0) {
        parts.push(`${outcome.notPlaceable} no longer open`)
      }
      if (remainder > 0) {
        parts.push(
          outcome.noRoom
            ? `${remainder} staged because no available day had room`
            : `${remainder} staged`,
        )
      }
      return `${cards} from "${title}" added — ${parts.join(', ')}.${already}`
    }
    default:
      return assertNever(destination, 'Unknown cascade destination')
  }
}

export interface CurriculumPersistenceApi {
  plan: QueryState<Plan>
  browse: BrowseCorpusState
  // Re-runs the browse query (debounced) with the given server-side filter
  // slice. A new slice is a new cache key, so the whole corpus re-drains for
  // it; the previous slice's rows stay on screen until the first page lands.
  setBrowseFilters: (input: BrowseFiltersInput) => void
  // Live study-plan names for the filter builder's plan dropdown.
  planOptions: string[]
  // Live problem sources (slug value + display label) for the filter
  // builder's source dropdown.
  sourceOptions: SourceFilterOption[]
  calendar: QueryState<Calendar>
  /**
   * The client's seen map (LG-362 §8): one row per problem this client has
   * ever seen, keyed by problemId. Kept separate from `browse` on purpose —
   * the corpus is client-agnostic and cached once, this is the only
   * per-client leg. `data: undefined` means "not arrived yet", which the
   * slice reports as `seenOverlayPending` so the pill's filter stays suspended
   * instead of briefly calling every row unseen.
   */
  seenSummary: QueryState<SeenSummary>
  stage: Command<'stage'>
  cascadeAssign: Command<'cascadeAssign'>
  setEstimate: Command<'setEstimate'>
  setPace: Command<'setPace'>
  place: Command<'place'>
  unplace: Command<'unplace'>
  move: Command<'move'>
  distribute: Command<'distribute'>
  optimize: Command<'optimize'>
  publishWeek: Command<'publishWeek'>
  unpublishWeek: Command<'unpublishWeek'>
  deleteWeek: Command<'deleteWeek'>
  addWeek: Command<'addWeek'>
  setBudget: Command<'setBudget'>
  convertAiMock: Command<'convertAiMock'>
  revertAiMock: Command<'revertAiMock'>
  linkAiMock: Command<'linkAiMock'>
  setTargetCompany: Command<'setTargetCompany'>
  cancel: Command<'cancel'>
  setCoachNote: Command<'setCoachNote'>
  setUniversalNotes: Command<'setUniversalNotes'>
  completeManual: Command<'completeManual'>
  // A point lookup fired on demand when the details modal opens, not a
  // continuously-reactive query like `plan`/`browse`/`calendar` — there is no
  // caching/pagination/mutation-coordination need to justify a `useQuery`
  // here, so it takes the same imperative `.fetch()` shape `loadMorePlan`
  // already uses for the same reason. `getCardDetails`, not `cardDetails`:
  // the slice, not this api layer, owns *when* to call it (LG-360 §7).
  getCardDetails: Command<'getCardDetails'>
  // The same on-demand shape for the modal's review read (LG-360 §3), which
  // is a second point lookup rather than a field on the one above: it is
  // keyed on the assignment, not the problem, so cards with no problem
  // behind them — every system-design and behavioral card — still get one.
  getCardReview: (
    input: ReviewInputs['getForAssignment'],
  ) => Promise<ReviewOutputs['getForAssignment']>
  setSource: Command<'setSource'>
  replace: Command<'replace'>
  invalidatePlanCaches: (clientVisible: boolean) => Promise<void>
  // Write a placement mutation's returned {assignment, week} straight into the
  // plan cache so a drag/drop lands immediately, instead of blocking the drop
  // on a full plan refetch. Reconcile peripheral caches separately, in the
  // background, via `invalidatePlanPeripheral`.
  applyPlacementResult: (result: PlacementResult) => void
  // Same idea for "+ Add week": a purely additive structural edit the server
  // describes completely in its return, so the board lands it without a
  // refetch.
  applyWeekAdded: (result: AddWeekResult) => void
  // And for "Delete week" (LG-543), which is the same trade with one extra
  // obligation: the delete control is rendered for every *merged* week, so the
  // patch has to reach the extra range and tray pages too, not just the cache
  // page `setData` targets.
  applyWeekDeleted: (result: DeleteWeekResult) => void
  invalidatePlanPeripheral: (clientVisible: boolean) => void
  isLoadingMorePlan: boolean
  loadMorePlan: () => Promise<void>
  planHistoryHasMore: boolean
  loadEarlierPlan: () => Promise<void>
  isLoadingMoreTray: boolean
  loadMoreTray: () => Promise<void>
  coverage?: { plan: boolean; tray: boolean; addWeek?: boolean }
}

export type UseCurriculumPersistenceApi = (
  clientId: string,
) => CurriculumPersistenceApi

// One message for all four week-shape commands (add, delete, publish,
// unpublish), since they share a single in-flight guard: naming a specific
// command here would tell a coach mid-delete that a week is being added.
//
// The guard is one lane rather than one flag per week on purpose. Each
// command's own race is per-week — two clicks send the same `weekVersion` and
// the second CONFLICTs — but a lane also catches the cross-command races a
// per-week flag would miss, and these are deliberate, infrequent commands
// where a few hundred milliseconds of "try again in a moment" costs the coach
// nothing.
const WEEK_COMMAND_BUSY_MESSAGE =
  'Another week command is still running — try again in a moment.'

const ZERO_UUID = '00000000-0000-4000-8000-000000000000'
const DAY_MS = 86_400_000

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function defaultBounds(): {
  rangeStart: string
  rangeEndExclusive: string
} {
  const today = new Date()
  const midnight = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  )
  return {
    rangeStart: dateKey(new Date(midnight.getTime() - 14 * DAY_MS)),
    rangeEndExclusive: dateKey(new Date(midnight.getTime() + 78 * DAY_MS)),
  }
}

export function hasCompleteActionablePlanCoverage(
  bounds: { rangeStart: string; rangeEndExclusive: string },
  engagement: { sprintStartsOn: string; endsOn: string },
): boolean {
  // Bulk commands operate only on non-past draft weeks. Add-week resolves its
  // latest prior identity separately; this boundary only proves the future.
  return bounds.rangeEndExclusive >= engagement.endsOn.slice(0, 10)
}

export function nextPlanRange(
  currentRangeEndExclusive: string,
  engagementEndsOn: string,
): { rangeStart: string; rangeEndExclusive: string } | null {
  const engagementEnd = engagementEndsOn.slice(0, 10)
  if (currentRangeEndExclusive >= engagementEnd) return null
  const start = new Date(`${currentRangeEndExclusive}T00:00:00.000Z`)
  if (Number.isNaN(start.getTime())) return null
  const maxEnd = dateKey(new Date(start.getTime() + 92 * DAY_MS))
  return {
    rangeStart: currentRangeEndExclusive,
    rangeEndExclusive: maxEnd < engagementEnd ? maxEnd : engagementEnd,
  }
}

/**
 * `planFloorOn`, not `sprintStartsOn`: materialization backfills weeks from the
 * anchored engagement start, which can precede the sprint origin, and those
 * weeks would be unreachable if paging stopped at the origin.
 */
export function previousPlanRange(
  currentRangeStart: string,
  planFloorOn: string,
): { rangeStart: string; rangeEndExclusive: string } | null {
  const floor = planFloorOn.slice(0, 10)
  if (currentRangeStart <= floor) return null
  const end = new Date(`${currentRangeStart}T00:00:00.000Z`)
  if (Number.isNaN(end.getTime())) return null
  const minStart = dateKey(new Date(end.getTime() - 92 * DAY_MS))
  return {
    rangeStart: minStart > floor ? minStart : floor,
    rangeEndExclusive: currentRangeStart,
  }
}

export function isCurrentPlanLoad(
  startedGeneration: number,
  currentGeneration: number,
  startedFingerprint: string | null,
  currentFingerprint: string | null,
): boolean {
  return (
    startedGeneration === currentGeneration &&
    startedFingerprint === currentFingerprint
  )
}

/**
 * The board status for a server week status, given whether the week's dates
 * have elapsed.
 *
 * Exhaustive over `WeekStatus` (LG-586). A fourth member added to that enum
 * fails the build here, where the ternary chain this replaced ended in a
 * catch-all that would have absorbed it as `'past'` silently — which is
 * exactly how `closed` came to be handled by accident rather than decision.
 *
 * `assertNever` rather than simply omitting the default: the producer stopped
 * validating this string when it stopped widening it, so nothing between the
 * database and this function would catch a value outside the enum. Falling
 * out of the return type is a compile-time guard only — at runtime an
 * out-of-union status would return `undefined`, which is neither `'past'`
 * (so `isPastWeek` reads false and the read-only gate opens) nor `'draft'`,
 * i.e. a week that must not be edited becomes quietly half-editable.
 *
 * `closed` maps to `'past'`, which is what the catch-all already did. It is
 * written out here so the mapping is a choice on the record: a
 * published-then-closed week is one the client can still see, and the fold
 * logic below relies on that (it keys on the *server* status precisely so a
 * closed week is never folded away).
 */
function boardWeekStatus(
  status: Plan['weeks'][number]['status'],
  elapsed: boolean,
): Week['status'] {
  switch (status) {
    case 'draft':
      return elapsed ? 'past' : 'draft'
    case 'published':
      // A published week that has been and gone still reports `published`:
      // the client saw it, and it stays read-only for that reason.
      return 'published'
    case 'closed':
      return 'past'
    default:
      return assertNever(status, 'Unknown plan week status')
  }
}

// Exported for the test that pins the reason `applyWeekAdded` has to re-stamp
// the extra loaded pages.
export function planFingerprint(plan: Plan | undefined): string | null {
  if (!plan) return null
  return JSON.stringify({
    engagementId: plan.engagement.id,
    weeks: plan.weeks.map((week) => [week.id, week.version, week.status]),
    assignments: plan.assignments.map((assignment) => [
      assignment.id,
      assignment.version,
      assignment.planDayId,
    ]),
    trayAssignmentIds: plan.trayAssignmentIds,
    nextTrayCursor: plan.nextTrayCursor,
  })
}

/**
 * Carry a week-shape patch across one collection of paged-in plan pages.
 *
 * Shared by `applyWeekAdded` and `applyWeekDeleted` — the two commands that
 * patch the plan cache rather than refetch it — because they need the same
 * two-part guard, and it is a guard that is easy to get half-right: LG-543
 * wrote it on the delete side, and the add side went on carrying neither half
 * until LG-595.
 *
 * `planFingerprint` covers `weeks`, so *any* week-shape command moves it, and
 * leaving the stamps alone would silently discard every extra plan range and
 * tray page the coach paged in — with no refetch left to bring them back,
 * because neither success path invalidates `getPlan`. Re-stamping keeps them.
 *
 * Only pages stamped for the plan we just patched, though. A page whose stamp
 * already disagreed with the base — the base refetched on its own and moved
 * on — is orphaned, and the board has already stopped merging it; stamping it
 * with the new fingerprint would resurrect that stale snapshot, versions and
 * all.
 *
 * `patchPage` is the half that genuinely differs between the two commands. A
 * delete is exactly as true of a paged-in range as of the base one, so those
 * pages take the same patch. An add appends a week strictly after the latest
 * one and lands in the base page alone, so its pages re-stamp unchanged: the
 * week does not belong to those pages' date ranges, and `mergePlanRangePages`
 * unions weeks by id across every page anyway, so patching them in would be
 * redundant work rather than a visible duplicate.
 */
export function applyToLoadedPlanPages<P>(
  current: { fingerprint: string | null; pages: P[] },
  // One named pair rather than two positional `string | null`s: transposing
  // them type-checks, and the result is silent total page loss — the guard
  // would then only fire on a collection already carrying the post-patch
  // stamp, which never happens, so every paged-in page is dropped on every
  // week command with no refetch to bring it back.
  stamp: { from: string | null; to: string | null },
  patchPage: (page: P) => P,
): { fingerprint: string | null; pages: P[] } {
  if (current.fingerprint === null) return current
  if (current.fingerprint !== stamp.from) return current
  return {
    fingerprint: stamp.to,
    pages: current.pages.map(patchPage),
  }
}

export function mergeTrayPages(
  first: Plan | undefined,
  pages: Plan[],
): Plan | undefined {
  if (!first) return undefined
  if (pages.length === 0) return first

  const assignmentById = new Map(
    first.assignments.map((assignment) => [assignment.id, assignment]),
  )
  const trayAssignmentIds = [...first.trayAssignmentIds]
  const seenTrayIds = new Set(trayAssignmentIds)
  for (const page of pages) {
    const pageTrayIds = new Set(page.trayAssignmentIds)
    for (const assignment of page.assignments) {
      if (pageTrayIds.has(assignment.id)) {
        assignmentById.set(assignment.id, assignment)
      }
    }
    for (const id of page.trayAssignmentIds) {
      if (!seenTrayIds.has(id)) {
        seenTrayIds.add(id)
        trayAssignmentIds.push(id)
      }
    }
  }

  return {
    ...first,
    assignments: [...assignmentById.values()],
    trayAssignmentIds,
    nextTrayCursor: pages.at(-1)!.nextTrayCursor,
  }
}

export function mergePlanRangePages(
  first: Plan | undefined,
  pages: Plan[],
): Plan | undefined {
  if (!first) return undefined
  if (pages.length === 0) return first

  const mergeById = <T extends { id: string }>(
    initial: T[],
    additions: T[][],
  ): T[] => {
    const values = new Map(initial.map((value) => [value.id, value]))
    for (const page of additions) {
      for (const value of page) {
        if (!values.has(value.id)) values.set(value.id, value)
      }
    }
    return [...values.values()]
  }

  const weeks = mergeById(
    first.weeks,
    pages.map((page) => page.weeks),
  ).sort(
    (left, right) =>
      left.startsOn.localeCompare(right.startsOn) ||
      left.id.localeCompare(right.id),
  )
  const days = mergeById(
    first.days,
    pages.map((page) => page.days),
  ).sort(
    (left, right) =>
      left.dayDate.localeCompare(right.dayDate) ||
      left.id.localeCompare(right.id),
  )
  const assignments = mergeById(
    first.assignments,
    pages.map((page) => page.assignments),
  )

  return { ...first, weeks, days, assignments }
}

/**
 * A placement mutation's return: the updated assignment (new planDayId +
 * version) and, when the operation touched a week, that week's new version.
 * `place` and `unplace` return exactly this; `move` returns it plus
 * `sourceWeek`, which is why that field is optional here rather than a second
 * type.
 */
export type PlacementResult = {
  assignment: { id: string; planDayId: string | null; version: number }
  week: { id: string; version: number } | null
  /**
   * The week a cross-week move emptied a slot in. Its version moved too, so the
   * cache has to take both or the next write against the source week would go
   * out with a version the server has already advanced past.
   *
   * Optional rather than required because only `move` produces it — `place`,
   * `unplace` and the two optimistic literals below return the narrower shape.
   */
  sourceWeek?: { id: string; version: number } | null
}

/**
 * Apply a placement mutation's returned delta to a cached plan so the schedule
 * reflects a drop without a server refetch. Only the fields the mutation
 * changes are patched onto the existing cached assignment/week (so there is no
 * shape mismatch with the fuller getPlan rows), and the assignment is moved to
 * the end of the list — within-day order is array order, and the server
 * appends a moved/placed card to the end of its target day. Tray membership
 * follows planDayId: on a day ⇒ out of the tray, off a day ⇒ in it.
 *
 * If the assignment is not in this cache page (e.g. a far-future week only
 * loaded as an extra range), the plan is returned unchanged and the move
 * settles on the next natural refetch.
 */
export function applyPlanAssignment(
  plan: Plan | undefined,
  { assignment, week, sourceWeek }: PlacementResult,
): Plan | undefined {
  if (!plan) return plan
  const existing = plan.assignments.find((row) => row.id === assignment.id)
  if (!existing) return plan
  const patched = {
    ...existing,
    planDayId: assignment.planDayId,
    version: assignment.version,
  }
  const assignments = [
    ...plan.assignments.filter((row) => row.id !== assignment.id),
    patched,
  ]
  // A cross-week move bumps the week the card left as well as the one it
  // joined, and both new versions have to land or the stale one is what the
  // next write sends.
  const bumped = new Map(
    [week, sourceWeek ?? null]
      .filter((entry) => entry !== null)
      .map((entry) => [entry.id, entry.version]),
  )
  const weeks = bumped.size
    ? plan.weeks.map((row) => {
        const version = bumped.get(row.id)
        return version === undefined ? row : { ...row, version }
      })
    : plan.weeks
  const onDay = assignment.planDayId !== null
  const inTray = plan.trayAssignmentIds.includes(assignment.id)
  const trayAssignmentIds = onDay
    ? plan.trayAssignmentIds.filter((id) => id !== assignment.id)
    : inTray
      ? plan.trayAssignmentIds
      : [...plan.trayAssignmentIds, assignment.id]
  return { ...plan, assignments, weeks, trayAssignmentIds }
}

/**
 * An `addWeek` mutation's return: the freshly inserted week and its seven
 * days, exactly as the server built them.
 */
export type AddWeekResult = {
  // `clientActivity` omitted for the same reason `note` is omitted from the
  // days below: it is a projection `getPlan` computes, not a column the
  // `addWeek` wire record carries.
  week: Omit<Plan['weeks'][number], 'clientActivity'>
  // `note` is absent: `PlanningDayRecord` does not carry it, and `addWeek`
  // inserts all seven days with a null note anyway.
  days: readonly Omit<Plan['days'][number], 'note'>[]
}

/**
 * Append a newly added week to a cached plan.
 *
 * `addWeek` only ever extends the plan forward — the service derives the new
 * week number from the latest existing week and rejects anything that isn't
 * strictly after it — so the week goes on the end and no existing row is
 * touched. Nothing else in the plan changes: no assignment moves, no budget
 * shifts, no other week's version bumps.
 *
 * The insert is idempotent against a refetch that already landed it, so a
 * refetch racing the mutation cannot produce a duplicate week.
 */
export function applyPlanWeekAdded(
  plan: Plan | undefined,
  { week, days }: AddWeekResult,
): Plan | undefined {
  if (!plan) return plan
  if (plan.weeks.some((row) => row.id === week.id)) return plan
  // Both rows are projected field by field rather than spread: the wire
  // records carry `engagementId`/`publishedByUserId`/`deletedAt` that the plan
  // DTOs do not, and a `deletedAt` sitting on one cached week would read as
  // authoritative to any future soft-delete-aware selector.
  return {
    ...plan,
    weeks: [
      ...plan.weeks,
      {
        id: week.id,
        sprintId: week.sprintId,
        weekNumber: week.weekNumber,
        startsOn: week.startsOn,
        status: week.status,
        publishedAt: week.publishedAt,
        version: week.version,
        // False by construction: the week was created microseconds ago, so
        // there is no client work on it to find. The server would compute the
        // same on the next natural refetch.
        clientActivity: false,
      },
    ],
    days: [
      ...plan.days,
      // Sorted by date: `getPlan` and `mergePlanRangePages` both order days
      // that way and the board renders a week's columns straight from array
      // order, but `INSERT … RETURNING` carries no ORDER BY. Since the success
      // path never refetches, a reordered RETURNING would otherwise leave the
      // week rendered with shuffled weekdays for the life of the cache entry.
      ...[...days]
        .sort((left, right) => left.dayDate.localeCompare(right.dayDate))
        .map((day) => ({
          id: day.id,
          weekId: day.weekId,
          dayDate: day.dayDate,
          kind: day.kind,
          budgetMinutes: day.budgetMinutes,
          note: null,
        })),
    ],
  }
}

/**
 * A `deleteWeek` mutation's return: the week that is gone, and every
 * assignment the delete unplaced off it — each with the version the unplace
 * left it at and the status that decides whether the tray shows it.
 */
export type DeleteWeekResult = Outputs['deleteWeek']

/**
 * Which statuses `getPlan` will still show once a card is off every day —
 * `ACTIONABLE_TRAY_STATUSES` in `lib/coaching/curriculum-query.ts`, restated
 * because that module reaches the database and cannot be imported here.
 *
 * The patch below has to leave the plan looking exactly like the refetch it
 * replaces, and `deleteWeek` unplaces submitted cards too — an unplaced
 * submitted card is in neither the tray nor any day, so it leaves the plan
 * altogether. Spelled as an exhaustive record rather than a set of strings so
 * that a sixth `AssignmentStatus` has to answer this question at compile time:
 * defaulting a new status to "not in the tray" here would make the delete drop
 * live cards off the board.
 */
const SHOWS_IN_TRAY_UNPLACED = {
  planned: true,
  in_progress: true,
  submitted: false,
  completed: false,
  cancelled: false,
} satisfies Record<Plan['assignments'][number]['status'], boolean>

/**
 * Remove a deleted week from a cached plan and land its assignments where the
 * server just put them.
 *
 * Written to be applied to *every* page the board merges — the base cache page,
 * each extra plan range, each extra tray page — because the delete control is
 * rendered for merged weeks, and the week's own rows live in whichever page
 * covers its dates. Each page therefore takes the whole result and keeps only
 * what applies to it:
 *
 * - the week and its days go, wherever they are;
 * - an assignment sitting on one of those days is either unplaced onto the
 *   tray (at the version the server returned) or dropped, exactly as `getPlan`
 *   would project it after the delete: cancelled rows keep pointing at a day
 *   that is gone, and completed/submitted rows unplace into no tray, so a
 *   refetch shows none of the three;
 * - the tray ids are appended for every moved-and-actionable assignment,
 *   including ones whose row is in a *different* page, because
 *   `mergePlanRangePages` keeps only the base page's `trayAssignmentIds` while
 *   taking rows from every page. Appending an id whose row no page holds is
 *   harmless — `scheduleState` renders the tray from the ids it can resolve.
 *
 * - the weeks the delete slid on to an earlier Sprint (LG-1073) take their
 *   new `startsOn` and `version`, and their days move by the same number of
 *   days, so the board shows them where the server now has them without a
 *   refetch.
 *
 * Idempotent: re-applying finds no days to remove, no id to append and no
 * week behind the returned version, and returns the same plan object, so a
 * patch racing a refetch cannot double up.
 */
export function applyPlanWeekDeleted(
  plan: Plan | undefined,
  { weekId, movedAssignments, reboundWeeks }: DeleteWeekResult,
): Plan | undefined {
  if (!plan) return plan
  const removedDayIds = new Set(
    plan.days.filter((day) => day.weekId === weekId).map((day) => day.id),
  )
  // Only a week this page holds at an older version moves; one already at
  // the returned version was landed by an earlier application or a refetch.
  const shiftByWeekId = new Map<
    string,
    { startsOn: string; sprintId: string; version: number; deltaMs: number }
  >()
  for (const rebound of reboundWeeks) {
    const week = plan.weeks.find((candidate) => candidate.id === rebound.id)
    if (!week || week.version >= rebound.version) continue
    shiftByWeekId.set(rebound.id, {
      startsOn: rebound.startsOn,
      sprintId: rebound.sprintId,
      version: rebound.version,
      deltaMs: Date.parse(rebound.startsOn) - Date.parse(week.startsOn),
    })
  }
  // Doubles as the survival test below: an id missing from here is one a
  // refetch would not project at all, so the row goes rather than lands.
  const trayVersionById = new Map(
    movedAssignments
      .filter((moved) => SHOWS_IN_TRAY_UNPLACED[moved.status])
      .map((moved) => [moved.id, moved.version]),
  )
  const addedTrayIds = [...trayVersionById.keys()].filter(
    (id) => !plan.trayAssignmentIds.includes(id),
  )
  if (
    removedDayIds.size === 0 &&
    addedTrayIds.length === 0 &&
    shiftByWeekId.size === 0
  ) {
    return plan
  }
  return {
    ...plan,
    weeks: plan.weeks.flatMap((week) => {
      if (week.id === weekId) return []
      const shift = shiftByWeekId.get(week.id)
      return [
        shift
          ? {
              ...week,
              startsOn: shift.startsOn,
              sprintId: shift.sprintId,
              version: shift.version,
            }
          : week,
      ]
    }),
    days: plan.days.flatMap((day) => {
      if (day.weekId === weekId) return []
      const shift = shiftByWeekId.get(day.weekId)
      return [
        shift
          ? {
              ...day,
              dayDate: new Date(
                Date.parse(day.dayDate) + shift.deltaMs,
              ).toISOString(),
            }
          : day,
      ]
    }),
    assignments: plan.assignments.flatMap((assignment) => {
      if (
        assignment.planDayId === null ||
        !removedDayIds.has(assignment.planDayId)
      ) {
        return [assignment]
      }
      const version = trayVersionById.get(assignment.id)
      // Nothing to land it at: the row is one a post-delete `getPlan` drops.
      if (version === undefined) return []
      return [{ ...assignment, planDayId: null, version }]
    }),
    // Appended, like `applyPlanAssignment` appends a single unplaced card, so
    // until the next refetch these ids sit at the *end* of the tray — the
    // schedule builds the tray in `trayAssignmentIds` order, so that is where
    // the coach sees them. The tray's real order is the server's: the delete
    // stamps every moved card `sortOrder: 0`, so once a refetch lands they
    // move to the top. This patch does not try to guess that order locally.
    trayAssignmentIds: [...plan.trayAssignmentIds, ...addedTrayIds],
  }
}

function mergeCalendarRangePages(first: Calendar, pages: Calendar[]): Calendar {
  const overlays = new Map(
    first.map((overlay) => [
      `${overlay.sourceKind}\u0000${overlay.sourceId}`,
      overlay,
    ]),
  )
  for (const page of pages) {
    for (const overlay of page) {
      overlays.set(`${overlay.sourceKind}\u0000${overlay.sourceId}`, overlay)
    }
  }
  return [...overlays.values()]
}

function dateKeyInTimeZone(date: Date, timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date)
    const value = (kind: Intl.DateTimeFormatPartTypes): string | undefined =>
      parts.find((part) => part.type === kind)?.value
    const year = value('year')
    const month = value('month')
    const day = value('day')
    return year && month && day ? `${year}-${month}-${day}` : null
  } catch {
    return null
  }
}

/**
 * Whether the week's seven days are all behind us in the engagement's zone.
 *
 * Asked of the dates and not the status, because that is what the service
 * does: `assertWeekNotPast` gates every editing command — and `unpublishWeek`
 * — whatever the week's status, and a published week keeps reporting
 * `published` long after it has been and gone.
 *
 * An unreadable zone or start date reads as elapsed, which is the recoverable
 * answer in both directions: the week goes read-only (rather than accepting
 * writes onto dates nobody can place), and if it is also empty it folds —
 * behind a disclosure that hands it straight back.
 */
function hasWeekElapsed(
  week: Plan['weeks'][number],
  timeZone: string,
  now: Date,
): boolean {
  const today = dateKeyInTimeZone(now, timeZone)
  if (today === null) return true
  const startsOn = new Date(week.startsOn)
  if (Number.isNaN(startsOn.getTime())) return true
  const endDateExclusive = dateKey(new Date(startsOn.getTime() + 7 * DAY_MS))
  return today >= endDateExclusive
}

function isNonPastDraftWeek(
  week: Plan['weeks'][number],
  timeZone: string,
  now: Date,
): boolean {
  return week.status === 'draft' && !hasWeekElapsed(week, timeZone, now)
}

// Structural mirror of the only invalidators this function touches: the
// real `trpc.useUtils()` satisfies it, and callers (incl. tests) need not
// produce the full utils surface.
export interface CurriculumMutationInvalidators {
  admin: {
    clients: {
      getById: { invalidate: (input: { id: string }) => Promise<unknown> }
      list: { invalidate: () => Promise<unknown> }
    }
    coachingV2: {
      curriculum: {
        getPlan: { invalidate: () => Promise<unknown> }
        sourceLibrary: { invalidate: () => Promise<unknown> }
      }
      overview: {
        get: { invalidate: (input: { clientId: string }) => Promise<unknown> }
      }
      reviews: {
        listWeeks: { invalidate: () => Promise<unknown> }
        listWeekWork: { invalidate: () => Promise<unknown> }
      }
    }
  }
  dashboard: { plan: { list: { invalidate: () => Promise<unknown> } } }
}

export async function invalidateCurriculumMutationQueries(
  utils: CurriculumMutationInvalidators,
  clientId: string,
  clientVisible: boolean,
): Promise<void> {
  const invalidations: Promise<unknown>[] = [
    utils.admin.clients.getById.invalidate({ id: clientId }),
    utils.admin.clients.list.invalidate(),
    utils.admin.coachingV2.curriculum.getPlan.invalidate(),
    // Sources tab derives `alreadyAssigned` from this query's cached
    // learningItemId; without invalidating it here, a container that gets
    // its LearningItem lazily created by this mutation still reads as
    // unassigned, so a second click stages the same chapter twice.
    utils.admin.coachingV2.curriculum.sourceLibrary.invalidate(),
    // ClientHeader / ProfileEditorDialog read `engagement.version` from this
    // query. setPace shares the same optimistic-concurrency column as
    // profile.update, so without invalidating it here, the header keeps the
    // pre-bump version and the next profile save sends a stale
    // expectedVersion, coming back a spurious CONFLICT.
    utils.admin.coachingV2.overview.get.invalidate({ clientId }),
    // The Review tab's sections are the plan's own weeks (LG-646), so adding,
    // deleting, publishing or unpublishing one changes the spine it renders —
    // the section list itself, and the Draft/Published pill on each header.
    // Rows are unaffected: they are filed by when the work happened, not by
    // where the card sits. The tab keeps its boundary mounted with its queries
    // disabled while the coach is on Curriculum, so a cached spine outlives
    // the write unless it is invalidated here.
    utils.admin.coachingV2.reviews.listWeeks.invalidate(),
    // The rows, not just the spine (LG-905). A curriculum write can now change
    // what the week *holds*: ticking a card writes a submission and an
    // awaiting review, un-ticking or cancelling supersedes the one it had.
    // Both halves are read from the same cache the tab keeps mounted, so
    // refreshing one and not the other is how a header count and a row list
    // come to disagree.
    utils.admin.coachingV2.reviews.listWeekWork.invalidate(),
  ]
  if (clientVisible) {
    invalidations.push(utils.dashboard.plan.list.invalidate())
  }
  await Promise.all(invalidations)
}

export function kindsForTypeFilter(
  filter: TypeFilter,
): BrowseFiltersInput['kinds'] {
  if (filter === 'coding') return ['problem', 'coding_exercise']
  if (filter === 'sd') return ['system_design']
  if (filter === 'behavioral') return ['behavioral']
  return undefined
}

// Keystrokes in the filter builder settle before the corpus-wide query
// re-runs.
const BROWSE_FILTER_DEBOUNCE_MS = 300

const sameBrowseKinds = (
  left: BrowseFiltersInput['kinds'],
  right: BrowseFiltersInput['kinds'],
): boolean => {
  if (left === right) return true
  if (!left || !right || left.length !== right.length) return false
  return left.every((kind, index) => kind === right[index])
}

export function useTrpcCurriculumPersistenceApi(
  clientId: string,
): CurriculumPersistenceApi {
  const bounds = useMemo(defaultBounds, [])
  // The base plan query's input, shared with the mutation cache-writes below so
  // `getPlan.setData` targets the exact same query key.
  const planQueryInput = useMemo(
    () => ({ clientId, ...bounds, trayLimit: 100, trayAfter: null }),
    [bounds, clientId],
  )
  const utils = trpc.useUtils()
  // Both start from the default type pill, normalized so the slice's
  // mount-time sync hashes identically and does not trigger a second,
  // differently-filtered first request.
  const [pendingBrowseFilters, setPendingBrowseFilters] =
    useState<BrowseFiltersInput>(() =>
      normalizeBrowseFilters({
        kinds: kindsForTypeFilter(DEFAULT_TYPE_FILTER),
      }),
    )
  const [activeBrowseFilters, setActiveBrowseFilters] =
    useState<BrowseFiltersInput>(pendingBrowseFilters)
  useEffect(() => {
    // The type pill (kinds) is a discrete choice — apply it immediately so the
    // table doesn't sit empty through the debounce that exists for filter-value
    // typing. Only the filter tree waits out the debounce.
    setActiveBrowseFilters((current) =>
      sameBrowseKinds(current.kinds, pendingBrowseFilters.kinds)
        ? current
        : { ...current, kinds: pendingBrowseFilters.kinds },
    )
    const timer = window.setTimeout(
      () =>
        setActiveBrowseFilters((current) => ({
          ...current,
          filters: pendingBrowseFilters.filters,
        })),
      BROWSE_FILTER_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(timer)
  }, [pendingBrowseFilters])
  const setBrowseFilters = useCallback((input: BrowseFiltersInput) => {
    setPendingBrowseFilters(normalizeBrowseFilters(input))
  }, [])
  const filterOptionsQuery =
    trpc.admin.coachingV2.curriculum.browseFilterOptions.useQuery()
  const planOptions = useMemo(
    () => filterOptionsQuery.data?.planNames ?? [],
    [filterOptionsQuery.data],
  )
  const sourceOptions = useMemo<SourceFilterOption[]>(
    () =>
      (filterOptionsQuery.data?.sources ?? []).map((entry) => ({
        value: entry.slug,
        label: SOURCE_ABBREVIATIONS[entry.slug] ?? entry.name,
      })),
    [filterOptionsQuery.data],
  )
  const [additionalPlanRanges, setAdditionalPlanRanges] = useState<{
    fingerprint: string | null
    pages: Array<{
      rangeStart: string
      rangeEndExclusive: string
      plan: Plan
      calendar: Calendar
    }>
  }>({ fingerprint: null, pages: [] })
  const [isLoadingMorePlan, setIsLoadingMorePlan] = useState(false)
  const planRangeLoadInFlight = useRef(false)
  const planLoadGeneration = useRef(0)
  const [additionalTrayPages, setAdditionalTrayPages] = useState<{
    fingerprint: string | null
    pages: Plan[]
  }>({ fingerprint: null, pages: [] })
  const [isLoadingMoreTray, setIsLoadingMoreTray] = useState(false)
  const trayLoadInFlight = useRef(false)
  const firstPlanPage =
    trpc.admin.coachingV2.curriculum.getPlan.useQuery(planQueryInput)
  const firstPlanFingerprint = planFingerprint(firstPlanPage.data)
  const firstPlanFingerprintRef = useRef(firstPlanFingerprint)
  firstPlanFingerprintRef.current = firstPlanFingerprint
  const compatiblePlanRanges =
    additionalPlanRanges.fingerprint === firstPlanFingerprint
      ? additionalPlanRanges.pages
      : []
  const compatibleTrayPages =
    additionalTrayPages.fingerprint === firstPlanFingerprint
      ? additionalTrayPages.pages
      : []
  const planData = useMemo(() => {
    const ranged = mergePlanRangePages(
      firstPlanPage.data,
      compatiblePlanRanges.map((page) => page.plan),
    )
    return mergeTrayPages(ranged, compatibleTrayPages)
  }, [compatiblePlanRanges, compatibleTrayPages, firstPlanPage.data])
  const plan = { ...firstPlanPage, data: planData }
  // The raw tRPC client, not `utils.….fetch`: the corpus is cached once, by
  // the infinite query, rather than also page-by-page under tRPC's own keys.
  const fetchBrowsePage = useCallback<BrowsePageFetcher>(
    (input) =>
      utils.client.admin.coachingV2.curriculum.browseLearningItems.query(input),
    [utils],
  )
  const browse = useBrowseCorpus(activeBrowseFilters, fetchBrowsePage)
  // The per-client overlay next to the client-agnostic corpus above: tRPC
  // keys this by its input, so switching clients refetches only this small
  // map, never the corpus.
  const seenSummary = trpc.admin.coachingV2.curriculum.seenSummary.useQuery({
    clientId,
  })
  const loadMoreTray = useCallback(async (): Promise<void> => {
    const cursor = planData?.nextTrayCursor ?? null
    if (
      cursor === null ||
      !planData ||
      firstPlanFingerprint === null ||
      trayLoadInFlight.current
    ) {
      return
    }
    const generation = planLoadGeneration.current
    const fingerprint = firstPlanFingerprint
    trayLoadInFlight.current = true
    setIsLoadingMoreTray(true)
    try {
      const page = await utils.admin.coachingV2.curriculum.getPlan.fetch({
        clientId,
        engagementId: planData.engagement.id,
        ...bounds,
        trayLimit: 100,
        trayAfter: cursor,
      })
      if (
        !isCurrentPlanLoad(
          generation,
          planLoadGeneration.current,
          fingerprint,
          firstPlanFingerprintRef.current,
        )
      ) {
        return
      }
      setAdditionalTrayPages((current) => ({
        fingerprint,
        pages:
          current.fingerprint === fingerprint
            ? [...current.pages, page]
            : [page],
      }))
    } finally {
      trayLoadInFlight.current = false
      setIsLoadingMoreTray(false)
    }
  }, [bounds, clientId, firstPlanFingerprint, planData, utils])
  const engagementId = plan.data?.engagement.id ?? ZERO_UUID
  const firstCalendarPage = trpc.admin.coachingV2.curriculum.calendar.useQuery(
    { clientId, engagementId, ...bounds },
    { enabled: plan.data !== undefined },
  )
  const calendarData = useMemo(
    () =>
      firstCalendarPage.data
        ? mergeCalendarRangePages(
            firstCalendarPage.data,
            compatiblePlanRanges.map((page) => page.calendar),
          )
        : undefined,
    [compatiblePlanRanges, firstCalendarPage.data],
  )
  const calendar = { ...firstCalendarPage, data: calendarData }

  const loadedRangeStart = compatiblePlanRanges.reduce(
    (earliest, page) =>
      page.rangeStart < earliest ? page.rangeStart : earliest,
    bounds.rangeStart,
  )
  const loadedRangeEndExclusive = compatiblePlanRanges.reduce(
    (latest, page) =>
      page.rangeEndExclusive > latest ? page.rangeEndExclusive : latest,
    bounds.rangeEndExclusive,
  )
  const loadPlanRange = useCallback(
    async (range: {
      rangeStart: string
      rangeEndExclusive: string
    }): Promise<void> => {
      if (
        !planData ||
        firstPlanFingerprint === null ||
        planRangeLoadInFlight.current
      ) {
        return
      }
      const generation = planLoadGeneration.current
      const fingerprint = firstPlanFingerprint
      planRangeLoadInFlight.current = true
      setIsLoadingMorePlan(true)
      try {
        const [nextPlan, nextCalendar] = await Promise.all([
          utils.admin.coachingV2.curriculum.getPlan.fetch({
            clientId,
            engagementId: planData.engagement.id,
            ...range,
            trayLimit: 100,
            trayAfter: null,
          }),
          utils.admin.coachingV2.curriculum.calendar.fetch({
            clientId,
            engagementId: planData.engagement.id,
            ...range,
          }),
        ])
        if (
          !isCurrentPlanLoad(
            generation,
            planLoadGeneration.current,
            fingerprint,
            firstPlanFingerprintRef.current,
          )
        ) {
          return
        }
        setAdditionalPlanRanges((current) => ({
          fingerprint,
          pages:
            current.fingerprint === fingerprint
              ? [
                  ...current.pages,
                  {
                    rangeStart: range.rangeStart,
                    rangeEndExclusive: range.rangeEndExclusive,
                    plan: nextPlan,
                    calendar: nextCalendar,
                  },
                ]
              : [
                  {
                    rangeStart: range.rangeStart,
                    rangeEndExclusive: range.rangeEndExclusive,
                    plan: nextPlan,
                    calendar: nextCalendar,
                  },
                ],
        }))
      } finally {
        planRangeLoadInFlight.current = false
        setIsLoadingMorePlan(false)
      }
    },
    [clientId, firstPlanFingerprint, planData, utils],
  )
  const loadMorePlan = useCallback(async (): Promise<void> => {
    if (
      !planData ||
      firstPlanFingerprint === null ||
      planRangeLoadInFlight.current
    ) {
      return
    }
    const next = nextPlanRange(
      loadedRangeEndExclusive,
      planData.engagement.endsOn,
    )
    if (!next) return
    await loadPlanRange(next)
  }, [firstPlanFingerprint, loadPlanRange, loadedRangeEndExclusive, planData])
  const loadEarlierPlan = useCallback(async (): Promise<void> => {
    if (!planData || firstPlanFingerprint === null) return
    const previous = previousPlanRange(
      loadedRangeStart,
      planData.engagement.planFloorOn,
    )
    if (!previous) return
    await loadPlanRange(previous)
  }, [firstPlanFingerprint, loadPlanRange, loadedRangeStart, planData])

  const stage = trpc.admin.coachingV2.curriculum.stage.useMutation()
  const cascadeAssign =
    trpc.admin.coachingV2.curriculum.cascadeAssign.useMutation()
  const setEstimate = trpc.admin.coachingV2.curriculum.setEstimate.useMutation()
  const setPace = trpc.admin.coachingV2.curriculum.setPace.useMutation()
  const place = trpc.admin.coachingV2.curriculum.place.useMutation()
  const unplace = trpc.admin.coachingV2.curriculum.unplace.useMutation()
  const move = trpc.admin.coachingV2.curriculum.move.useMutation()
  const distribute = trpc.admin.coachingV2.curriculum.distribute.useMutation()
  const optimize = trpc.admin.coachingV2.curriculum.optimize.useMutation()
  const publishWeek = trpc.admin.coachingV2.curriculum.publishWeek.useMutation()
  const unpublishWeek =
    trpc.admin.coachingV2.curriculum.unpublishWeek.useMutation()
  const deleteWeek = trpc.admin.coachingV2.curriculum.deleteWeek.useMutation()
  const addWeek = trpc.admin.coachingV2.curriculum.addWeek.useMutation()
  const setBudget = trpc.admin.coachingV2.curriculum.setBudget.useMutation()
  const convertAiMock =
    trpc.admin.coachingV2.curriculum.convertAiMock.useMutation()
  const revertAiMock =
    trpc.admin.coachingV2.curriculum.revertAiMock.useMutation()
  const linkAiMock = trpc.admin.coachingV2.curriculum.linkAiMock.useMutation()
  const setTargetCompany =
    trpc.admin.coachingV2.curriculum.setTargetCompany.useMutation()
  const cancel = trpc.admin.coachingV2.curriculum.cancel.useMutation()
  const setCoachNote =
    trpc.admin.coachingV2.curriculum.setCoachNote.useMutation()
  const setUniversalNotes =
    trpc.admin.coachingV2.curriculum.setUniversalNotes.useMutation()
  const completeManual =
    trpc.admin.coachingV2.curriculum.completeManual.useMutation()
  const setSource = trpc.admin.coachingV2.curriculum.setSource.useMutation()
  const replace = trpc.admin.coachingV2.curriculum.replace.useMutation()
  // `useCallback`-wrapped for the same reason `loadMorePlan`/`loadEarlierPlan`
  // are: `usePersistedCurriculumSlice`'s fetch effect depends on this binding
  // (not the whole `api` object, which is a fresh object literal every render
  // of this hook), so an unmemoized arrow here would refire that effect —
  // and refetch the same open card's details — on every unrelated
  // query/mutation state change in this hook.
  const getCardDetails = useCallback<
    CurriculumPersistenceApi['getCardDetails']
  >(
    (input) => utils.admin.coachingV2.curriculum.getCardDetails.fetch(input),
    [utils],
  )
  // Memoized for the same reason, and depended on by its own fetch effect in
  // `usePersistedCurriculumSlice`.
  //
  // `staleTime: 0` because this is the one read on the board whose data the
  // *other* tab rewrites: a coach can open a card, jump to the Review tab
  // (the schedule card offers that jump), claim the review, write comments
  // and finish it, then reopen the same card. `fetch` is `fetchQuery`, which
  // serves a cached entry without a request while it is fresh, and the
  // provider's default keeps every entry fresh for 30s — long enough for
  // that round trip, so the modal would confidently render "Waiting for
  // review · No comments" over a review the coach had just finished.
  const getCardReview = useCallback<CurriculumPersistenceApi['getCardReview']>(
    (input) =>
      utils.admin.coachingV2.reviews.getForAssignment.fetch(input, {
        staleTime: 0,
      }),
    [utils],
  )

  const invalidatePlanCaches = useCallback(
    async (clientVisible: boolean): Promise<void> => {
      planLoadGeneration.current += 1
      setAdditionalPlanRanges({ fingerprint: null, pages: [] })
      setAdditionalTrayPages({ fingerprint: null, pages: [] })
      await invalidateCurriculumMutationQueries(utils, clientId, clientVisible)
    },
    [clientId, utils],
  )

  const applyPlacementResult = useCallback(
    (result: PlacementResult): void => {
      // The same three guards `applyWeekAdded` and `applyWeekDeleted` carry.
      // This is the third sibling and went without them until LG-614: a
      // placement moves `assignments[].planDayId`, `assignments[].version` and
      // `trayAssignmentIds`, and `planFingerprint` covers all three, so every
      // drop moved the stamp and silently discarded every paged-in range and
      // tray page — with no refetch to bring them back, because `mutation()`'s
      // apply branch deliberately never invalidates `getPlan`. The coach saw
      // it as "+ Add week" and the bulk actions disabling themselves after one
      // card drop (`loadedRangeEndExclusive` falls back to the base bounds, so
      // `planComplete` goes false).
      //
      // Scoped to this exact input, which is the only query the patch writes:
      // an unscoped cancel matches the whole `getPlan` key, aborting the
      // one-shot `.fetch()` a concurrent `loadPlanRange`/`loadMoreTray` has
      // out — those reject, and the coach reads "More plan dates could not be
      // loaded" over a drop that in fact succeeded.
      void utils.admin.coachingV2.curriculum.getPlan.cancel(planQueryInput)
      // Retires every pagination load already in flight, before the patch
      // below moves the fingerprint they were stamped against. Placement takes
      // the delete side of this choice, not the add side: an add appends a
      // week strictly after the latest one and invalidates nothing already
      // loaded, but a page read before this placement still shows the card on
      // the day it just left, so dropping it is the honest outcome. One
      // re-click restores it.
      planLoadGeneration.current += 1
      let patched: Plan | undefined
      let previousFingerprint: string | null = null
      utils.admin.coachingV2.curriculum.getPlan.setData(
        planQueryInput,
        (old) => {
          previousFingerprint = planFingerprint(old)
          patched = applyPlanAssignment(old, result)
          return patched
        },
      )
      // Re-stamped, **not** patched — the opposite of the delete path, and the
      // difference is the merge, not the command.
      //
      // `mergeTrayPages` only adopts a page's assignment row while that row's
      // id is still in *that page's* `trayAssignmentIds`, and
      // `mergePlanRangePages` keeps only the base page's tray ids. So patching
      // a paged-in page loses cards outright: place a card that lives in tray
      // page 2 and the patch drops its id from that page's tray, the merge
      // then declines to adopt the row, and the card is in neither the tray
      // nor its target day until a reload. Unplacing a card whose row lives in
      // a paged-in range is the mirror image — the tray id is appended to a
      // page the merge does not read tray ids from.
      //
      // `applyPlanWeekDeleted` can patch its pages because it appends tray ids
      // to *every* page for exactly this reason; `applyPlanAssignment` cannot,
      // because it early-returns for a page that lacks the row and so can
      // never add one.
      //
      // Re-stamping alone is what LG-614 is actually for: it keeps the coach's
      // paged-in pages instead of silently discarding them. A paged-in range
      // still showing the card on its old day is stale until the next natural
      // refetch, which is strictly better than losing every page — and far
      // better than losing the card.
      const fingerprint = planFingerprint(patched)
      setAdditionalPlanRanges((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => page,
        ),
      )
      setAdditionalTrayPages((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => page,
        ),
      )
    },
    [planQueryInput, utils],
  )

  const applyWeekAdded = useCallback(
    (result: AddWeekResult): void => {
      // A getPlan refetch that started before the mutation would otherwise
      // resolve after it and overwrite the patch wholesale — and because the
      // success path no longer invalidates getPlan, nothing would re-derive
      // it. The coach would see the success flash over an unchanged board,
      // then CONFLICT on the next "+ Add week" because the server's latest
      // week is now the one the board never rendered.
      //
      // Scoped to this exact input, which is the only query the patch writes:
      // an unscoped cancel matches the whole `getPlan` key, aborting the
      // one-shot `.fetch()` a concurrent `loadPlanRange`/`loadMoreTray` has
      // out — those reject, and the coach reads "More plan dates could not be
      // loaded" over an add that in fact succeeded.
      void utils.admin.coachingV2.curriculum.getPlan.cancel(planQueryInput)
      // Retires every pagination load already in flight, before the patch
      // below moves the fingerprint they were stamped against.
      //
      // `firstPlanFingerprintRef` only catches up on the next render, so a
      // `loadMoreTray`/`loadPlanRange` whose fetch resolves between the
      // `setData` below and that render still reads the *old* fingerprint out
      // of the ref, passes `isCurrentPlanLoad`, and stamps its own page with
      // that old fingerprint — while the collection it writes into has
      // already been re-stamped with the patched one. The updater sees the
      // mismatch, replaces the coach's paged-in pages with `[page]`, and the
      // next render throws the whole collection away as incompatible with the
      // base. Every extra page the coach loaded would vanish on an add that
      // succeeded.
      //
      // Unlike the delete case, the dropped in-flight page was not *stale* —
      // an add invalidates nothing already loaded. It is dropped because it
      // cannot be stamped: it has not arrived yet, so there is nothing to
      // re-stamp, and letting it land under the pre-add fingerprint is what
      // costs the coach the whole collection. One re-click restores it.
      planLoadGeneration.current += 1
      let patched: Plan | undefined
      let previousFingerprint: string | null = null
      utils.admin.coachingV2.curriculum.getPlan.setData(
        planQueryInput,
        (old) => {
          previousFingerprint = planFingerprint(old)
          patched = applyPlanWeekAdded(old, result)
          return patched
        },
      )
      // Re-stamped, not patched: `addWeek` appends a week strictly after the
      // latest one and touches no assignment, so it belongs to the base page
      // alone and nothing already paged in goes stale. See
      // `applyToLoadedPlanPages` for why the stamp has to move at all, and why
      // only pages the base still owns may take the new one.
      const fingerprint = planFingerprint(patched)
      setAdditionalPlanRanges((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => page,
        ),
      )
      setAdditionalTrayPages((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => page,
        ),
      )
    },
    [planQueryInput, utils],
  )

  const applyWeekDeleted = useCallback(
    (result: DeleteWeekResult): void => {
      // Same in-flight refetch hazard `applyWeekAdded` cancels for: with no
      // getPlan invalidation on the success path, a response that started
      // before the delete would overwrite the patch and nothing would
      // re-derive it — leaving the deleted week on the board under a success
      // flash, at a version the server has already moved past.
      //
      // Scoped to this exact input, which is the only query the patch writes:
      // an unscoped cancel matches the whole `getPlan` key, aborting the
      // one-shot `.fetch()` a concurrent `loadPlanRange`/`loadMoreTray` has
      // out — those reject, and the coach reads "More plan dates could not be
      // loaded" over a delete that in fact succeeded.
      void utils.admin.coachingV2.curriculum.getPlan.cancel(planQueryInput)
      // Retires every pagination load already in flight, before the patch
      // below moves the fingerprint they were stamped against.
      //
      // `firstPlanFingerprintRef` only catches up on the next render, so a
      // `loadMoreTray`/`loadPlanRange` whose fetch resolves between the
      // `setData` below and that render still reads the *old* fingerprint out
      // of the ref, passes `isCurrentPlanLoad`, and stamps its own page with
      // that old fingerprint — while the collection it writes into has
      // already been re-stamped with the patched one. The updater sees the
      // mismatch, replaces the coach's paged-in pages with `[page]`, and the
      // next render throws the whole collection away as incompatible with the
      // base. Every extra page the coach loaded would vanish on a delete that
      // succeeded. The generation is the half of `isCurrentPlanLoad` no
      // render has to commit for, so bumping it here closes that window; the
      // in-flight page is dropped, which is the honest outcome anyway — it
      // was read before the delete and still holds the deleted week.
      planLoadGeneration.current += 1
      let patched: Plan | undefined
      let previousFingerprint: string | null = null
      utils.admin.coachingV2.curriculum.getPlan.setData(
        planQueryInput,
        (old) => {
          previousFingerprint = planFingerprint(old)
          patched = applyPlanWeekDeleted(old, result)
          return patched
        },
      )
      // Patched *and* re-stamped: the delete is exactly as true of a page the
      // coach paged in as of the base one. See `applyToLoadedPlanPages` for
      // why the stamp has to move, and why only pages the base still owns may
      // take the new one.
      const fingerprint = planFingerprint(patched)
      setAdditionalPlanRanges((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => {
            const next = applyPlanWeekDeleted(page.plan, result)
            return next === undefined ? page : { ...page, plan: next }
          },
        ),
      )
      setAdditionalTrayPages((current) =>
        applyToLoadedPlanPages(
          current,
          { from: previousFingerprint, to: fingerprint },
          (page) => applyPlanWeekDeleted(page, result) ?? page,
        ),
      )
    },
    [planQueryInput, utils],
  )

  const invalidatePlanPeripheral = useCallback(
    (clientVisible: boolean): void => {
      // The schedule already reflects the change via `applyPlacementResult`, so
      // these peripheral caches (client badges, dashboard plan list) settle in
      // the background without the drop waiting on them — and the plan itself
      // is deliberately not refetched.
      void utils.admin.clients.getById.invalidate({ id: clientId })
      void utils.admin.clients.list.invalidate()
      if (clientVisible) void utils.dashboard.plan.list.invalidate()
      // Nothing on the Review tab moves when a card does. It files each row
      // under the week the client *worked* it, not the week the card is
      // scheduled for (LG-646, ADR 0013), so a placement — which is only ever
      // a change to the schedule — cannot change which section a review sits
      // under. This used to invalidate the queue for exactly that reason
      // (LG-642); the reason is gone, and re-reading the tab after every drop
      // would now be work for no difference.
    },
    [clientId, utils],
  )

  const engagement = plan.data?.engagement
  const planComplete = Boolean(
    engagement &&
      hasCompleteActionablePlanCoverage(
        { ...bounds, rangeEndExclusive: loadedRangeEndExclusive },
        engagement,
      ),
  )
  const historicalPlanComplete = Boolean(
    engagement && loadedRangeStart <= engagement.planFloorOn.slice(0, 10),
  )
  const latestWeekIdentityKnown = Boolean(
    plan.data && (plan.data.weeks.length > 0 || historicalPlanComplete),
  )
  const addWeekComplete = planComplete && latestWeekIdentityKnown
  const planHistoryHasMore = Boolean(
    engagement && plan.data?.weeks.length === 0 && !historicalPlanComplete,
  )

  return {
    plan,
    browse,
    setBrowseFilters,
    planOptions,
    sourceOptions,
    calendar,
    seenSummary: {
      data: seenSummary.data,
      isLoading: seenSummary.isLoading,
      error: seenSummary.error,
      refetch: seenSummary.refetch,
    },
    stage: stage.mutateAsync,
    cascadeAssign: cascadeAssign.mutateAsync,
    setEstimate: setEstimate.mutateAsync,
    setPace: setPace.mutateAsync,
    place: place.mutateAsync,
    unplace: unplace.mutateAsync,
    move: move.mutateAsync,
    distribute: distribute.mutateAsync,
    optimize: optimize.mutateAsync,
    publishWeek: publishWeek.mutateAsync,
    unpublishWeek: unpublishWeek.mutateAsync,
    deleteWeek: deleteWeek.mutateAsync,
    addWeek: addWeek.mutateAsync,
    setBudget: setBudget.mutateAsync,
    convertAiMock: convertAiMock.mutateAsync,
    revertAiMock: revertAiMock.mutateAsync,
    linkAiMock: linkAiMock.mutateAsync,
    setTargetCompany: setTargetCompany.mutateAsync,
    cancel: cancel.mutateAsync,
    setCoachNote: setCoachNote.mutateAsync,
    setUniversalNotes: setUniversalNotes.mutateAsync,
    completeManual: completeManual.mutateAsync,
    getCardDetails,
    getCardReview,
    setSource: setSource.mutateAsync,
    replace: replace.mutateAsync,
    invalidatePlanCaches,
    applyPlacementResult,
    applyWeekAdded,
    applyWeekDeleted,
    invalidatePlanPeripheral,
    isLoadingMorePlan,
    loadMorePlan,
    planHistoryHasMore,
    loadEarlierPlan,
    isLoadingMoreTray,
    loadMoreTray,
    coverage: {
      plan: planComplete,
      tray: plan.data?.nextTrayCursor === null,
      addWeek: addWeekComplete,
    },
  }
}

function displayType(value: string, track: string): TrayCard['type'] {
  if (track === 'coding') return 'coding'
  if (['sd', 'system-design', 'system_design'].includes(track)) return 'sd'
  if (track === 'behavioral') return 'behavioral'
  if (track === 'interview') return 'interview'
  if (['PROBLEM', 'EXERCISE'].includes(value)) return 'coding'
  if (value === 'SYSTEM_DESIGN') return 'sd'
  return 'other'
}

function catalogType(
  kind: Browse['items'][number]['kind'],
): LibraryItem['type'] {
  if (['problem', 'coding_exercise'].includes(kind)) return 'coding'
  if (kind === 'system_design') return 'sd'
  if (kind === 'behavioral') return 'behavioral'
  return 'other'
}

const SOURCE_ABBREVIATIONS: Record<string, string> = {
  leetcode: 'LC',
  'beyond-cracking-the-coding-interview': 'BCtCI',
  'cracking-the-coding-interview': 'CtCI',
  'grokking-the-coding-interview-patterns': 'GCI',
}

function sourceLabel(item: Browse['items'][number]): string | undefined {
  if (item.sourceSlug === null) return undefined
  return SOURCE_ABBREVIATIONS[item.sourceSlug] ?? item.sourceSlug
}

const COLUMN_STORAGE_KEY = 'bctci:curriculum:browse-columns'

interface StoredColumns {
  hidden: string[]
  order: string[]
}

const storedStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []

const readStoredColumns = (): StoredColumns =>
  readLocalPreference<StoredColumns>(
    COLUMN_STORAGE_KEY,
    (raw) => {
      const parsed: unknown = JSON.parse(raw)
      if (parsed === null || typeof parsed !== 'object') return null
      const record = parsed as Record<string, unknown>
      return {
        hidden: storedStrings(record.hidden),
        order: storedStrings(record.order),
      }
    },
    { hidden: [], order: [] },
  )

const writeStoredColumns = (columns: StoredColumns): void =>
  writeLocalPreference(COLUMN_STORAGE_KEY, JSON.stringify(columns))

// Statuses that still occupy the engagement's "one active assignment per
// learning item" slot. Completed and cancelled assignments free the slot so
// a finished problem can be staged again as a redo.
const INCOMPLETE_ASSIGNMENT_STATUSES = new Set([
  'planned',
  'in_progress',
  'submitted',
])

/**
 * One parsed seen-map entry, keyed by problemId (see `seenByProblemId`):
 * the wire row with its date revived. The type derives from the transport
 * shape and the entry is built by spreading the row, so a field added to
 * the wire arrives here without anyone remembering to copy it, and a
 * widened `via` cannot leave this copy silently narrower.
 */
type SeenRow = SeenSummary[number]
type SeenEntry = Omit<SeenRow, 'problemId' | 'lastSeenAt'> & {
  lastSeenAt: Date | null
}

function libraryItems(
  items: Browse['items'],
  activeLearningItemIds: ReadonlySet<string>,
  // `undefined` means the overlay has not arrived yet — rows then carry no
  // seen fields at all, and the slice reports seenOverlayPending.
  seenByProblemId: ReadonlyMap<string, SeenEntry> | undefined,
): LibraryItem[] {
  return items.map((item) => {
    const seenEntry =
      item.kind === 'problem' ? seenByProblemId?.get(item.catalogId) : undefined
    const seenFields =
      seenByProblemId === undefined
        ? {}
        : {
            seen: seenEntry !== undefined,
            lastSeenAt: seenEntry?.lastSeenAt ?? null,
            seenProvenance: seenEntry?.provenance,
            seenVia: seenEntry?.via,
          }
    return {
      ...libraryItemBase(item, activeLearningItemIds),
      ...seenFields,
    }
  })
}

function libraryItemBase(
  item: Browse['items'][number],
  activeLearningItemIds: ReadonlySet<string>,
): LibraryItem {
  const primaryTopicPair = item.topics.find((pair) => pair.isPrimary) ?? null
  return {
    id: item.id,
    learningItemId: item.id,
    catalogKind: item.kind,
    catalogId: item.catalogId,
    title: item.title,
    type: catalogType(item.kind),
    // The real taxonomy, one pair per tagged approach (LG-362 §1). The
    // flattened pair is the primary one; the array is what the column stacks
    // and the filters fold over, and it stays present-but-empty for an
    // untagged problem so those rows read as untagged rather than as mock
    // rows carrying a single pair.
    //
    // Found by the server's marker, never by position: a problem whose first
    // approach is untagged has no primary pair, and the em dashes are the
    // honest answer for it even though `topicPairs` below is not empty.
    topic: primaryTopicPair?.topic ?? '—',
    subtopic: primaryTopicPair?.subtopic ?? '—',
    topicPairs: item.topics,
    stage: item.track,
    est: item.estimatedMinutes === null ? 0 : item.estimatedMinutes / 60,
    estimatedMinutes: item.estimatedMinutes,
    difficulty: item.difficulty ?? '—',
    elo: item.eloRating === null ? null : Math.round(item.eloRating),
    eloRating: item.eloRating,
    extId: item.externalId,
    source: sourceLabel(item),
    sources: item.sources,
    alreadyAssigned: activeLearningItemIds.has(item.id),
    plans: [],
    companies: [],
  }
}

function dayLabel(value: string): string {
  const date = new Date(value)
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  })
    .format(date)
    .toUpperCase()
  return `${weekday} ${date.getUTCDate()}`
}

/**
 * The Sprint context each week shows (LG-1073), keyed by week id.
 *
 * Read off `plan.sprints`, which the server lists in date order with every
 * participation the client holds -- skipped and unfunded ones included -- so
 * a gap between two weeks is exactly the Sprints that sit between their bound
 * Sprints in that list. A week bound to a Sprint the list does not name gets
 * no context rather than a wrong one.
 */
function weekSprintContexts(
  plan: Pick<Plan, 'sprints' | 'weeks'>,
  now: Date,
): Map<string, WeekSprintContext> {
  const indexById = new Map(
    plan.sprints.map((sprint, index) => [sprint.id, index] as const),
  )
  const weeks = [...plan.weeks].sort(
    (left, right) =>
      Date.parse(left.startsOn) - Date.parse(right.startsOn) ||
      left.weekNumber - right.weekNumber ||
      left.id.localeCompare(right.id),
  )
  const contexts = new Map<string, WeekSprintContext>()
  let previousIndex: number | null = null
  let reachDrawn = false
  for (const week of weeks) {
    const index = indexById.get(week.sprintId)
    if (index === undefined) continue
    const sprint = plan.sprints[index]!
    const gapsBefore: WeekSprintGap[] = []
    if (previousIndex !== null) {
      for (const between of plan.sprints.slice(previousIndex + 1, index)) {
        if (
          between.participationStatus === 'skipped' ||
          between.participationStatus === 'unfunded'
        ) {
          gapsBefore.push({
            sprintId: between.id,
            name: between.name,
            reason: between.participationStatus,
          })
        }
      }
    }
    previousIndex = index
    // Only a week still ahead of the client can be waiting on credits: a
    // locked week is already paid for and a skipped one is not theirs.
    const beyondReach =
      !reachDrawn && sprint.participationStatus === 'upcoming' && !sprint.funded
    if (beyondReach) reachDrawn = true
    contexts.set(week.id, {
      name: sprint.name,
      inFlight:
        sprint.participationStatus === 'locked' &&
        Date.parse(sprint.endsAt) > now.getTime(),
      reducedRequirements: sprint.reducedRequirements,
      gapsBefore,
      beyondReach,
      leftBehind:
        sprint.participationStatus === 'skipped' ||
        sprint.participationStatus === 'unfunded'
          ? sprint.participationStatus
          : null,
    })
  }
  return contexts
}

function weekLabel(week: Plan['weeks'][number]): string {
  const start = new Date(week.startsOn)
  const end = new Date(start.getTime() + 6 * DAY_MS)
  const month = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
  })
  const startMonth = month.format(start)
  const endMonth = month.format(end)
  return `Week ${week.weekNumber} · ${startMonth} ${start.getUTCDate()}–${startMonth === endMonth ? '' : `${endMonth} `}${end.getUTCDate()}`
}

function overlayCards(overlays: Calendar, dayDateKey: string): TrayCard[] {
  return overlays.flatMap((overlay) => {
    if (overlay.temporalKind === 'all_day') {
      if (
        dayDateKey < overlay.startDate ||
        dayDateKey >= overlay.endDateExclusive
      ) {
        return []
      }
      return [
        {
          id: `calendar:${overlay.sourceKind}:${overlay.sourceId}:${dayDateKey}`,
          title: overlay.title,
          type: 'interview' as const,
          meta: [overlay.subtitle, '📅 calendar · all day']
            .filter(Boolean)
            .join(' · '),
          est: 0,
          estimatedMinutes: 0,
          fixed: true,
        },
      ]
    }

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: overlay.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const parts = formatter.formatToParts(new Date(overlay.startsAt))
    const value = (kind: Intl.DateTimeFormatPartTypes): string =>
      parts.find((part) => part.type === kind)?.value ?? ''
    const startKey = `${value('year')}-${value('month')}-${value('day')}`
    if (startKey !== dayDateKey) return []
    return [
      {
        id: `calendar:${overlay.sourceKind}:${overlay.sourceId}`,
        title: overlay.title,
        type: 'interview' as const,
        meta: [overlay.subtitle, '📅 calendar'].filter(Boolean).join(' · '),
        est: 0,
        estimatedMinutes: 0,
        fixed: true,
      },
    ]
  })
}

function assignmentCard(
  assignment: Plan['assignments'][number],
  weeksByDay: ReadonlyMap<string, Plan['weeks'][number]>,
  estimateContext: { clientElo: number | null; pace: CoachingPace },
): TrayCard {
  const week = assignment.planDayId
    ? weeksByDay.get(assignment.planDayId)
    : undefined
  const derived = derivedEstimate({
    clientElo: estimateContext.clientElo,
    problemElo: assignment.problemEloRating,
    pace: estimateContext.pace,
    isRedo: assignment.repeatOfAssignmentId !== null,
  })
  const effective = effectiveEstimateMinutes({
    overrideMinutes: assignment.estimatedMinutes,
    clientElo: estimateContext.clientElo,
    problemElo: assignment.problemEloRating,
    pace: estimateContext.pace,
    isRedo: assignment.repeatOfAssignmentId !== null,
    catalogMinutes: assignment.catalogEstimatedMinutes,
  })
  return {
    id: assignment.id,
    title: assignment.coachTitle,
    type:
      assignment.presentationMode === 'ai_mock_hidden'
        ? 'interview'
        : displayType(assignment.compatibilityType, assignment.track),
    meta: `${assignment.track} · ~${formatMinutesDuration(effective)}`,
    est: effective / 60,
    estimatedMinutes: effective,
    estimateLow: derived?.low ?? null,
    estimateHigh: derived?.high ?? null,
    // "Overridden" whenever there is a computed value to reset to — under
    // the total estimate engine that includes the generic default, so any
    // library-backed row with a stored override gets the amber marker and
    // the ↺ reset. Rows without a learningItemId stay excluded: ai-mock and
    // custom assignments carry a deliberately *seeded/authored* value with
    // no derivation behind it, and resetting would swap it for the generic
    // default.
    estimateOverridden:
      assignment.estimatedMinutes !== null &&
      (derived !== null ||
        assignment.catalogEstimatedMinutes !== null ||
        assignment.learningItemId !== null),
    done: assignment.status === 'completed',
    aiMock: assignment.presentationMode === 'ai_mock_hidden',
    needsLink:
      assignment.presentationMode === 'ai_mock_hidden' &&
      assignment.aiMockLearningItemId === null,
    linkedItemId: assignment.aiMockLearningItemId ?? undefined,
    link: assignment.customUrl ?? undefined,
    version: assignment.version,
    learningItemId: assignment.learningItemId,
    problemId: assignment.problemId,
    externalId: assignment.externalId,
    planDayId: assignment.planDayId,
    weekId: week?.id ?? null,
    weekVersion: week?.version ?? null,
    targetCompanyId: assignment.targetCompanyId,
    status: assignment.status,
    sourceId: assignment.sourceId,
    sourceDisplayName: assignment.sourceDisplayName,
    sourceIcon: assignment.sourceIcon,
    sources: assignment.sources,
    completionMode: toCompletionMode(assignment.completionMode),
    instructions: assignment.instructions,
    compatibilityDescription: assignment.compatibilityDescription,
    coachNote: assignment.coachNote,
    universalNotes: assignment.universalNotes,
    creatingParent: assignment.creatingParent,
    otherMemberships: assignment.otherMemberships,
  }
}

/**
 * A board card that is backed by a real assignment row, and so carries the
 * version every write has to send.
 */
type VersionedCard = TrayCard & { version: number }

/**
 * Proves a card is an assignment before it can be written to.
 *
 * `TrayCard.version` is optional because the board also carries cards that are
 * not assignments — calendar overlays are synced events with no row of their
 * own. Every mutating procedure's input is `.strict()` and demands
 * `assignmentVersion`, so an overlay reaching one would send `undefined` and be
 * rejected by Zod with a message no coach can act on. This was previously a
 * `card.version!` inside `cardIdentity`, which type-checked precisely because
 * the assertion silenced the case it needed to catch; the guard against it was
 * a render-time convention (`schedule-card.tsx` withholds controls for
 * `card.fixed`), not anything the compiler could see.
 */
function withVersion(card: TrayCard | undefined): VersionedCard | null {
  if (!card) return null
  const { version } = card
  return version === undefined ? null : { ...card, version }
}

function scheduleState(
  plan: Plan,
  overlays: Calendar,
  now: Date,
): {
  // `open` is deliberately absent. Whether a week renders expanded depends on
  // the coach's toggles, which live in the hook — a mapper from server rows
  // has no way to answer it, and hardcoding a value here would be a field
  // guaranteed wrong for every folded week.
  weeks: Omit<Week, 'open'>[]
  tray: TrayCard[]
} {
  const estimateContext = {
    clientElo: plan.engagement.clientElo,
    pace: plan.engagement.pace,
  }
  const dayById = new Map(plan.days.map((day) => [day.id, day]))
  const weekById = new Map(plan.weeks.map((week) => [week.id, week]))
  const sprintContextByWeekId = weekSprintContexts(plan, now)
  const weekByDay = new Map(
    plan.days.flatMap((day) => {
      const week = weekById.get(day.weekId)
      return week ? ([[day.id, week]] as const) : []
    }),
  )
  const cardById = new Map(
    plan.assignments.map((assignment) => [
      assignment.id,
      assignmentCard(assignment, weekByDay, estimateContext),
    ]),
  )
  const weeks = plan.weeks.map((week): Omit<Week, 'open'> => {
    const days = plan.days
      .filter((day) => day.weekId === week.id)
      .map((day): ScheduleDay => {
        const key = day.dayDate.slice(0, 10)
        return {
          id: day.id,
          dateKey: key,
          label: dayLabel(day.dayDate),
          kind:
            day.kind === 'availability'
              ? 'avail'
              : (day.kind as ScheduleDay['kind']),
          budget: day.budgetMinutes / 60,
          note: day.note ?? undefined,
          cards: [
            ...plan.assignments
              .filter((assignment) => assignment.planDayId === day.id)
              .map((assignment) => cardById.get(assignment.id)!),
            ...overlayCards(overlays, key),
          ],
        }
      })
    const elapsed = hasWeekElapsed(week, plan.engagement.timeZone, now)
    const status: Week['status'] = boardWeekStatus(week.status, elapsed)
    return {
      id: week.id,
      version: week.version,
      label: weekLabel(week),
      status,
      sprint: sprintContextByWeekId.get(week.id),
      // Read from the server, not re-derived from the cards below (LG-590):
      // two of the three sources the server counts — the status-event ledger
      // and the submission table — are rows this DTO does not carry, so a
      // card scan here answers a strictly weaker question and produces an
      // Unpublish button that looks enabled on a week the server refuses.
      clientActivity: week.clientActivity,
      // Carried beside `status` because the two answer different questions: a
      // published week that has been and gone still reports `published` (the
      // client saw it, and it stays read-only for that reason), so the
      // schedule cannot ask "is this week behind us" of the status alone.
      elapsed,
      // A week that elapsed without anyone ever putting a card in it is pure
      // noise on a long engagement, so the schedule tucks it behind the
      // "show N earlier week(s)" disclosure. `published` folds alongside
      // `draft` (LG-544 §1): an all-empty published week has nothing
      // client-visible in it by construction, so there is no client-facing
      // work to hide.
      //
      // Keyed on the *server* status rather than the derived one above:
      // `boardWeekStatus` maps `closed` to `'past'` alongside elapsed drafts,
      // and a published-then-closed week is one the client can still see.
      // Folding one of those away would hide from the coach work the client
      // is looking at, which is why `closed` stays excluded here. Elapsing is
      // asked of the dates for the same reason: a published week never
      // derives to `past`.
      //
      // Emptiness is "no cards at all", not "no live tasks": a cancelled
      // assignment and a synced calendar event both still render, and hiding
      // a week the coach can see reads as data loss. The `days.length` guard
      // keeps a week that somehow arrived with no day rows visibly broken
      // instead of silently gone.
      folded:
        (week.status === 'draft' || week.status === 'published') &&
        elapsed &&
        days.length > 0 &&
        days.every((day) => day.cards.length === 0),
      days,
    }
  })
  const tray = plan.trayAssignmentIds.flatMap((id) => {
    const assignment = cardById.get(id)
    return assignment ? [assignment] : []
  })
  // Fail closed if an assignment points at a missing day/week; it remains
  // visible in the tray instead of silently disappearing from the UI.
  for (const assignment of plan.assignments) {
    if (assignment.planDayId !== null && !dayById.has(assignment.planDayId)) {
      const card = cardById.get(assignment.id)
      if (card) tray.push({ ...card, planDayId: null, weekId: null })
    }
  }
  return { weeks, tray }
}

function randomUuid(): string {
  return globalThis.crypto.randomUUID()
}

// Revealing the earlier weeks should read as a compact list of headers, not
// screens of empty day grids, so a folded week comes back collapsed. Stated
// once: the render and the toggle both consult it, and they cannot disagree
// about what the toggle is flipping away from.
const defaultWeekOpen = (week: Pick<Week, 'folded'>): boolean => !week.folded

function usePersistedCurriculumSlice(
  api: CurriculumPersistenceApi,
  clientId: string,
): CurriculumSlice {
  const plan = api.plan.data!
  const activeLearningItemIds = useMemo(() => {
    const ids = new Set<string>()
    for (const assignment of plan.assignments) {
      if (
        assignment.learningItemId !== null &&
        INCOMPLETE_ASSIGNMENT_STATUSES.has(assignment.status)
      ) {
        ids.add(assignment.learningItemId)
      }
    }
    return ids
  }, [plan.assignments])
  // Wire rows → parsed map once per overlay arrival, not once per row per
  // render. `undefined` (still loading) stays undefined so `libraryItems`
  // can tell "not seen" apart from "not known yet".
  const seenSummaryData = api.seenSummary.data
  const seenByProblemId = useMemo(() => {
    if (seenSummaryData === undefined) return undefined
    return new Map<string, SeenEntry>(
      seenSummaryData.map(({ problemId, ...row }) => [
        problemId,
        {
          ...row,
          lastSeenAt: row.lastSeenAt === null ? null : new Date(row.lastSeenAt),
        },
      ]),
    )
  }, [seenSummaryData])
  const items = useMemo(
    () =>
      libraryItems(api.browse.data!, activeLearningItemIds, seenByProblemId),
    [api.browse.data, activeLearningItemIds, seenByProblemId],
  )
  const serverState = useMemo(
    () => scheduleState(plan, api.calendar.data!, new Date()),
    [api.calendar.data, plan],
  )
  const [flash, setFlash] = useState<string | null>(null)
  const [filters, setFilters] = useState<CurriculumSlice['filters']>([])
  const [sorts, setSorts] = useState<SortSpec[]>([])
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(DEFAULT_TYPE_FILTER)
  const [companyTags, setCompanyTags] = useState<CompanyTag[]>([])
  const [initialStoredColumns] = useState(readStoredColumns)
  const [hiddenCols, setHiddenCols] = useState<string[]>(
    initialStoredColumns.hidden,
  )
  const [columnOrder, setColumnOrder] = useState<string[]>(
    initialStoredColumns.order,
  )
  // Exactly one writer knows the storage shape, and it always persists the
  // committed state — setters stay plain updates that cannot clobber each
  // other's half of the blob.
  const columnsHydrating = useRef(true)
  useEffect(() => {
    if (columnsHydrating.current) {
      columnsHydrating.current = false
      return
    }
    writeStoredColumns({ hidden: hiddenCols, order: columnOrder })
  }, [hiddenCols, columnOrder])
  // Coach-level viewing preference, shared across clients like the column
  // layout; the write happens in the cycle handler so there is exactly one
  // writer and no hydration-tick effect.
  const [seenFilter, setSeenFilter] = useState<SeenFilter>(readStoredSeenFilter)
  const [libraryTab, setLibraryTab] = useState<LibraryTab>('browse')
  // Sources-tab expansion (LG-312) — slice-held so it survives tab switches;
  // null until the coach first touches the tree (the tab's default view).
  const [sourceTreeOpen, setSourceTreeOpen] =
    useState<ReadonlySet<string> | null>(null)
  const [placingId, setPlacingId] = useState<string | null>(null)
  // Keep the server-evaluated browse query in step with the type pill and
  // the filter builder — this is what makes filters corpus-wide.
  const setBrowseFilters = api.setBrowseFilters
  useEffect(() => {
    setBrowseFilters({
      kinds: kindsForTypeFilter(typeFilter),
      filters: sanitizeBrowseFilters(filters),
    })
  }, [filters, setBrowseFilters, typeFilter])
  const [stagePending, setStagePending] = useState(false)
  const stageInFlight = useRef(false)
  // "+ Add week" and week delete both re-derive their expected version from
  // the latest week, so two overlapping ones always CONFLICT. The ref is what
  // actually guards (it is set before React re-renders); the state exists so
  // the button can go disabled while the command is out.
  const [weekShapePending, setWeekShapePending] = useState(false)
  const weekShapeInFlight = useRef(false)
  const [pacePending, setPacePending] = useState(false)
  const [removingIds, setRemovingIds] = useState<string[]>([])
  // Explicit toggles only. A plain "closed" set could not express the default
  // below, because an empty past week has to start collapsed while every other
  // week starts open — and a coach who opens one must keep it open.
  const [weekOpenOverrides, setWeekOpenOverrides] = useState<
    ReadonlyMap<string, boolean>
  >(() => new Map())
  const [stageRetry, setStageRetry] = useState<{
    label: string
    run: () => Promise<unknown>
  } | null>(null)
  const engagementId = plan.engagement.id
  // The coach's manual folds for this engagement, read once at mount —
  // `PersistedCurriculumActive` is keyed on the engagement, so another
  // client's board is a different mount with its own hydration.
  const [weekFolds, setWeekFolds] = useState<WeekFoldOverrides>(() =>
    readStoredWeekFolds(engagementId),
  )
  // The whole map is written every time, from the state that was committed —
  // one writer that cannot half-write the blob, like the column layout above.
  // An empty map is skipped rather than written: until the coach folds
  // something there is nothing to persist, and writing anyway would leave a
  // key behind for every client the coach merely opened.
  useEffect(() => {
    if (weekFolds.size === 0) return
    writeStoredWeekFolds(engagementId, weekFolds)
  }, [engagementId, weekFolds])

  // Which problem the details modal is asking about, or null when closed (or
  // when the open card has no catalog problem behind it — a one-off card has
  // nothing to have history, sources or relatives). The tab pushes the id in
  // via `setDetailsProblemId`; this hook owns the fetch because it is where
  // tRPC lives. `useState`'s setter is referentially stable across renders by
  // React's own contract, so it is exposed directly below rather than
  // rebuilt inside the slice's big recomputing `useMemo` — a `setDetailsProblemId`
  // whose identity churned would refire the tab's effect, and possibly loop.
  const [detailsProblemId, setDetailsProblemId] = useState<string | null>(null)
  const [cardDetails, setCardDetails] = useState<CardDetailsResult | null>(null)
  const [cardDetailsPending, setCardDetailsPending] = useState(false)
  // True only for a fetch that actually failed — distinct from the tick
  // before the first fetch for a newly-opened problem starts, which also has
  // `cardDetails === null` and `cardDetailsPending === false` but is not an
  // error. Cleared at the top of every fetch (including a retry of the same
  // problem) so a stale failure can never outlive the request that produced
  // it.
  const [cardDetailsError, setCardDetailsError] = useState(false)
  // Guards a superseded fetch from overwriting a later one — the coach can
  // close one card and open another before the first request lands.
  const detailsRequestId = useRef(0)
  // Extracted, and depended on below, rather than the whole `api` object —
  // same reason `setBrowseFilters` above is `api.setBrowseFilters` and not
  // `api` itself. `useTrpcCurriculumPersistenceApi` returns a fresh object
  // literal every render, so depending on `api` directly would refire this
  // effect (and refetch the same open card) on every unrelated query/mutation
  // state change in that hook, even though `getCardDetails` itself is stable
  // (see the `useCallback` wrapping it there).
  const getCardDetails = api.getCardDetails
  useEffect(() => {
    if (detailsProblemId === null) {
      setCardDetails(null)
      setCardDetailsPending(false)
      setCardDetailsError(false)
      return
    }
    const requestId = ++detailsRequestId.current
    setCardDetailsPending(true)
    setCardDetailsError(false)
    void (async () => {
      try {
        const result = await getCardDetails({
          clientId,
          problemId: detailsProblemId,
        })
        if (detailsRequestId.current !== requestId) return
        setCardDetails(result)
      } catch {
        if (detailsRequestId.current !== requestId) return
        setCardDetails(null)
        setCardDetailsError(true)
      } finally {
        if (detailsRequestId.current === requestId) {
          setCardDetailsPending(false)
        }
      }
    })()
  }, [getCardDetails, clientId, detailsProblemId])

  // Which card's review the details modal is asking about (LG-360 §3), or
  // null when closed. A second piece of state beside `detailsProblemId`
  // rather than a field folded into it: the two are keyed on different
  // things — a problem and an assignment — and a card with no problem still
  // has a review to ask about, so collapsing them would silently drop the
  // review read for every system-design and behavioral card.
  const [detailsAssignmentId, setDetailsAssignmentId] = useState<string | null>(
    null,
  )
  // One tagged state that always names the card it is about, rather than a
  // payload plus pending/error flags that can contradict each other — see
  // `CardReviewState`.
  const [cardReview, setCardReview] = useState<CardReviewState | null>(null)
  // Its own generation counter, not the details one: the two fetches are
  // independent, and sharing a counter would let a landing review response
  // be discarded by a details request that started after it.
  const reviewRequestId = useRef(0)
  // Bumped by a write that changes a card's review, to re-run the read below
  // (LG-905). `cardReview` is populated imperatively rather than by a
  // `useQuery`, so invalidating a cache key cannot reach it — and a tick now
  // mints a submission and an `awaiting` review where a manual card had none,
  // while an un-tick supersedes the one it had. Without this the coach ticks a
  // card and the section under the toggle still reads "Nothing submitted for
  // review yet" until they close and reopen the modal.
  //
  // It names the card it is about rather than being a bare counter. The write
  // is slow by construction — it awaits `completeManual` and then the whole
  // invalidation fan-out — and the modal stays closable throughout, so a
  // nameless bump could land after the coach had moved to another card and
  // refetch *that* one for a write that had nothing to do with it.
  const [reviewReload, setReviewReload] = useState<{
    assignmentId: string
    seq: number
  } | null>(null)
  // Only a reload that names the card currently open moves this, so a bump for
  // a card the coach has already left changes nothing.
  const reviewReloadSeq =
    reviewReload !== null && reviewReload.assignmentId === detailsAssignmentId
      ? reviewReload.seq
      : 0
  // Extracted for the same reason `getCardDetails` above is: the effect
  // depends on this binding, and depending on the whole `api` object — a
  // fresh literal every render of the hook — would refetch the open card's
  // review on every unrelated query or mutation state change.
  const getCardReview = api.getCardReview
  useEffect(() => {
    // Bumped even on the close path, so a response still in flight for the
    // card just closed cannot land afterwards and re-populate the state this
    // clears.
    const requestId = ++reviewRequestId.current
    if (detailsAssignmentId === null) {
      setCardReview(null)
      return
    }
    const assignmentId = detailsAssignmentId
    // `pending` only when this is a different card. A re-read of the card
    // already on screen keeps the answer it has until the fresh one replaces
    // it: a manual card in `pending` fails every arm of the modal's
    // `showReview` test, so resetting would unmount the whole Review section
    // and reflow the dialog for a round trip. The write has its own pending
    // affordance — the completion control's own "Saving…".
    setCardReview((current) =>
      current?.assignmentId === assignmentId
        ? current
        : { assignmentId, phase: 'pending' },
    )
    void (async () => {
      try {
        const result = await getCardReview({ clientId, assignmentId })
        if (reviewRequestId.current !== requestId) return
        // A `review: null` result is an answer, not an absence: this card
        // has nothing submitted. `loaded` is what says so.
        setCardReview({ assignmentId, phase: 'loaded', review: result.review })
      } catch {
        if (reviewRequestId.current !== requestId) return
        setCardReview({ assignmentId, phase: 'error' })
      }
    })()
  }, [getCardReview, clientId, detailsAssignmentId, reviewReloadSeq])

  const weeks = useMemo(
    () =>
      serverState.weeks.map((week) => {
        // `open` defaults off the *composed* fold, not the derived one: a week
        // the coach has just folded by hand should reveal collapsed too.
        const folded = foldedWeek(week, weekFolds)
        return {
          ...week,
          folded,
          open: weekOpenOverrides.get(week.id) ?? defaultWeekOpen({ folded }),
        }
      }),
    [serverState.weeks, weekFolds, weekOpenOverrides],
  )
  const tray = serverState.tray
  const placing = tray.find((card) => card.id === placingId) ?? null

  const refreshPlan = useCallback(
    async (clientVisible = false): Promise<void> => {
      await api.invalidatePlanCaches(clientVisible)
    },
    [api.invalidatePlanCaches],
  )

  const startStageOperation = useCallback(
    (operation: () => Promise<unknown>): boolean => {
      if (stageInFlight.current) return false
      stageInFlight.current = true
      setStagePending(true)
      void operation().finally(() => {
        stageInFlight.current = false
        setStagePending(false)
      })
      return true
    },
    [],
  )

  // Resolves true on success, false on failure (after the state refresh) so
  // optimistic controls know whether to keep or drop their pending value.
  const mutation = useCallback(
    async <T,>(
      operation: () => Promise<T>,
      success: string,
      options: { clientVisible?: boolean; apply?: (result: T) => void } = {},
    ): Promise<boolean> => {
      const { clientVisible = false, apply } = options
      try {
        const result = await operation()
        if (apply) {
          // Land the change immediately from the mutation's returned data and
          // reconcile peripheral caches in the background — the drop no longer
          // waits on a full plan refetch.
          apply(result)
          api.invalidatePlanPeripheral(clientVisible)
        } else {
          await refreshPlan(clientVisible)
        }
        setFlash(success)
        return true
      } catch (error) {
        let refreshed = false
        try {
          await refreshPlan(clientVisible)
          refreshed = true
        } catch {
          // The command may still have committed. Preserve that ambiguity and
          // require an explicit page reload instead of replaying automatically.
        }
        if (errorCode(error) === 'CONFLICT') {
          setFlash(
            refreshed
              ? 'This plan changed in another session. The latest server state has been loaded; your command was not retried.'
              : 'This plan changed in another session, but the latest state could not be loaded. Reload before trying again.',
          )
          return false
        }
        // "Could not be confirmed" is only true when the outcome is genuinely
        // unknown. A rejected command is a known outcome, and the server often
        // says why — report that instead of the shrug (LG-272).
        //
        // Keyed on retryability, the same predicate the stage paths below use.
        // Branching on `errorReason() !== null` here instead would put a code
        // that is neither explained nor retryable — TOO_MANY_REQUESTS — into
        // the shrug on this path and into the rejection on those.
        if (!isRetryableFailure(error)) {
          const reason =
            errorReason(error) ?? 'The server rejected the command.'
          setFlash(
            refreshed
              ? `The planning command was rejected. ${reason}`
              : `The planning command was rejected. ${reason} The latest server state could not be loaded — reload before trying again.`,
          )
          return false
        }
        setFlash(
          refreshed
            ? 'The planning command could not be confirmed. The latest server state was loaded and nothing was retried automatically.'
            : 'The planning command could not be confirmed and refresh failed. Reload before trying again.',
        )
        return false
      }
    },
    [api.invalidatePlanPeripheral, refreshPlan],
  )

  /**
   * The write path for the details modal, kept separate from `mutation` above.
   *
   * `mutation` reports through the board-level flash, which is right for a
   * command whose result you read on the board. These write into an open
   * dialog that covers it, so the outcome is returned to the caller and
   * rendered next to the field it belongs to. It also lets each caller name its
   * own conflict: a stale note and a stale plan are different accidents, and
   * "this plan changed in another session" is the wrong sentence for a coach
   * whose colleague just edited the same problem's notes.
   */
  const detailWrite = useCallback(
    async (
      operation: () => Promise<unknown>,
      conflictMessage: string,
    ): Promise<CardDetailsSaveResult> => {
      try {
        await operation()
      } catch (error) {
        // Resync either way: a failed response does not prove the write was
        // not committed, and the modal reads the card it just wrote. Whether
        // that resync worked is the difference between "the card in front of
        // you is server truth" and "reload before you trust any of this", so
        // it is carried into the message rather than swallowed.
        let refreshed = false
        try {
          await refreshPlan(true)
          refreshed = true
        } catch {
          // Preserve the ambiguity instead of replaying the write.
        }
        // A conflict is the one failure that *is* certain: the version check
        // rejected the command, so nothing was written.
        if (errorCode(error) === 'CONFLICT') return { error: conflictMessage }
        // A rejection is certain too — the server refused, and often says why,
        // so it does not belong in the "could not confirm" bucket either.
        // Keyed on retryability to match the other three catch sites.
        if (!isRetryableFailure(error)) {
          const reason =
            errorReason(error) ?? 'The server rejected the command.'
          return {
            error: refreshed
              ? `The save was rejected. ${reason}`
              : `The save was rejected. ${reason} The card could not be reloaded — reload the page before trying again.`,
          }
        }
        return {
          error: refreshed
            ? 'Could not confirm the save. The card has been reloaded — check it before trying again.'
            : 'Could not confirm the save, and the card could not be reloaded. Reload the page before trying again.',
        }
      }
      // The write landed. A refresh that fails from here leaves the board
      // stale, not the save undone, so it must not be reported as a failure.
      await refreshPlan(true).catch(() => {})
      return { error: null }
    },
    [refreshPlan],
  )

  // Used by the day-usage loop below — needs the same
  // override-then-derived-then-catalog resolution, not the raw
  // manual-override field, so a null override doesn't read as "no estimate"
  // when the elo curve (or the catalog) has one. `plan.assignments` entries
  // are the full `AdminAssignmentDto` projection (joined problemEloRating +
  // catalogEstimatedMinutes), so this stays pinned to that DTO shape. The
  // freshly-staged assignment in `runCatalogStage` is a *different* shape (a
  // raw `CoachingAssignment` row with neither field) and resolves its
  // estimate separately, from the originating `LibraryItem` — see there.
  const effectiveAssignmentMinutes = useCallback(
    (assignment: Plan['assignments'][number]): number =>
      effectiveEstimateMinutes({
        overrideMinutes: assignment.estimatedMinutes,
        clientElo: plan.engagement.clientElo,
        problemElo: assignment.problemEloRating,
        pace: plan.engagement.pace,
        isRedo: assignment.repeatOfAssignmentId !== null,
        catalogMinutes: assignment.catalogEstimatedMinutes,
      }),
    [plan.engagement],
  )

  /**
   * The first non-past draft day with room for `estimatedMinutes`.
   *
   * `committedByDay` is what a caller has already placed since this render's
   * `plan` was loaded, in minutes per day. A single add leaves it empty; a
   * cascade (LG-907) places several cards from one loaded plan, and without
   * this every one of them would be fitted against the same stale day usage
   * and pile onto the first day until the server refused the week — or, worse,
   * accepted it and left a day over budget, which is what blocks publishing.
   */
  const firstFit = useCallback(
    (
      estimatedMinutes: number,
      committedByDay: ReadonlyMap<string, number> = new Map(),
    ): { dayId: string; week: Plan['weeks'][number] } | null => {
      const now = new Date()
      for (const week of plan.weeks) {
        if (!isNonPastDraftWeek(week, plan.engagement.timeZone, now)) continue
        for (const day of plan.days.filter(
          (candidate) =>
            candidate.weekId === week.id && candidate.kind === 'normal',
        )) {
          let used = committedByDay.get(day.id) ?? 0
          for (const assignment of plan.assignments.filter(
            (candidate) =>
              candidate.planDayId === day.id &&
              candidate.status !== 'cancelled',
          )) {
            used += effectiveAssignmentMinutes(assignment)
          }
          if (used + estimatedMinutes <= day.budgetMinutes) {
            return { dayId: day.id, week }
          }
        }
      }
      return null
    },
    [
      effectiveAssignmentMinutes,
      plan.assignments,
      plan.days,
      plan.engagement.timeZone,
      plan.weeks,
    ],
  )

  const runCatalogStage = useCallback(
    async (
      item: Pick<
        LibraryItem,
        'title' | 'eloRating' | 'elo' | 'estimatedMinutes'
      >,
      destination: StageDestination,
      variables: Inputs['stage'],
    ): Promise<CardDetailsSaveResult> => {
      // Resolved once, ahead of both the placement attempt and the flash
      // text below, so the two never disagree about which day "the chosen
      // day" means.
      const chosenWeek =
        typeof destination === 'object'
          ? (weeks.find((week) => week.id === destination.weekId) ?? null)
          : null
      const chosenDay =
        typeof destination === 'object'
          ? (chosenWeek?.days.find((day) => day.id === destination.dayId) ??
            null)
          : null
      // An explicit day pick (an object `destination`, not `'auto'`) that no
      // longer resolves to a real week/day — deleted or renumbered between
      // when the coach picked it and when this runs — is a failure to
      // report, not a placement to silently skip. `'auto'`'s `firstFit`
      // finding nothing is different: the coach never committed to one
      // specific day there, so falling back to "staged, unplaced" is
      // honest. Here it would silently discard an explicit choice while
      // still reporting success — the exact fabricated-success this file's
      // own `RELATED_WRITE_NOT_BUILT` doctrine exists to reject.
      //
      // No `chosenDay.id === undefined` clause: `chosenDay` is only non-null
      // when its `id` matched a `string` `dayId`, so that check can never
      // fire. `chosenWeek.version` is genuinely optional and does.
      const pickedDayUnavailable =
        destination === 'picked-day-gone' ||
        (typeof destination === 'object' &&
          (chosenWeek === null ||
            chosenWeek.version === undefined ||
            chosenDay === null))
      // Set the instant `stage` commits, and read in the catch below. The two
      // `api.place` calls further down are inside the same `try`, so a failure
      // there is a failed *placement* of an assignment that already exists —
      // reporting it as "staging failed" would contradict the very board the
      // catch's own `refreshPlan()` is about to render the card onto.
      let staged = false
      try {
        const result = await api.stage(variables)
        staged = true
        setStageRetry(null)
        // Three states, not a boolean: "on a day" and "on a day *this run*
        // put it there" are different facts, and only the second licenses
        // naming which day. A creation-key replay can come back already
        // placed — by the lost original attempt, or by the coach dragging
        // the tray card somewhere else before clicking Retry — and that day
        // is not the one this call resolved.
        let placement: StagePlacement =
          result.assignment.planDayId != null ? 'already' : 'none'
        // The freshly-staged assignment is a raw `CoachingAssignment` row —
        // it has no joined problemEloRating/catalogEstimatedMinutes (those
        // only exist on the `AdminAssignmentDto` plan projection), so the
        // elo/catalog inputs come from the originating `LibraryItem`
        // instead, which was read from the same DB columns.
        //
        // Must be the exact `eloRating`, not the rounded `elo` used for
        // display: the server derives from full precision, and because the
        // curve output snaps to 5-minute steps a sub-point difference is
        // never small — it is either 0 or a whole step. A step of daylight
        // between the day this picks and the budget the server enforces at
        // publish is what makes the week unpublishable.
        const effectiveMinutes = effectiveEstimateMinutes({
          overrideMinutes: result.assignment.estimatedMinutes,
          clientElo: plan.engagement.clientElo,
          problemElo: item.eloRating ?? item.elo,
          pace: plan.engagement.pace,
          isRedo: result.assignment.repeatOfAssignmentId !== null,
          catalogMinutes: item.estimatedMinutes ?? null,
        })
        if (destination === 'auto' && placement === 'none') {
          const target = firstFit(effectiveMinutes)
          if (target) {
            await api.place({
              clientId,
              engagementId,
              assignmentId: result.assignment.id,
              assignmentVersion: result.assignment.version,
              planDayId: target.dayId,
              weekId: target.week.id,
              weekVersion: target.week.version,
            })
            placement = 'this-run'
          }
        } else if (
          typeof destination === 'object' &&
          placement === 'none' &&
          chosenWeek !== null &&
          chosenWeek.version !== undefined &&
          chosenDay !== null &&
          chosenDay.id !== undefined
        ) {
          await api.place({
            clientId,
            engagementId,
            assignmentId: result.assignment.id,
            assignmentVersion: result.assignment.version,
            planDayId: chosenDay.id,
            weekId: chosenWeek.id,
            weekVersion: chosenWeek.version,
          })
          placement = 'this-run'
        }
        await refreshPlan()
        if (pickedDayUnavailable && placement === 'none') {
          const message = `"${item.title}" was added to the staging tray, but the day you picked is no longer available. Reopen it to place on a current day.`
          setFlash(message)
          return { error: message }
        }
        // Every sentence here is chosen from `placement`, not `destination`
        // — see `stageOutcomeMessage`.
        setFlash(
          stageOutcomeMessage(item.title, destination, chosenDay, placement),
        )
        return { error: null }
      } catch (error) {
        let refreshed = false
        try {
          await refreshPlan()
          refreshed = true
        } catch {
          // Retry remains explicit and replays the same creation key.
        }
        // Both branches below drop any pending retry. This attempt may itself
        // *be* that retry, and it just proved the replay cannot succeed —
        // leaving the button up would re-offer the failure it just returned.
        if (errorCode(error) === 'CONFLICT') {
          setStageRetry(null)
          const message = refreshed
            ? 'This plan changed in another session. The latest server state has been loaded; your command was not retried.'
            : 'This plan changed in another session, but the latest state could not be loaded. Reload before trying again.'
          setFlash(message)
          return { error: message }
        }
        // Staging re-syncs the item's catalog entry, so a catalog defect
        // rejects it — and rejects the identical replay every time after
        // that. Offering a retry there is a lie, and the lie is what hid the
        // real reason behind "could not be confirmed" (LG-272).
        if (!isRetryableFailure(error)) {
          setStageRetry(null)
          const reason =
            errorReason(error) ?? 'The server rejected the command.'
          // "did not finish" rather than "could not be placed": the `try`
          // also covers the post-stage refresh, so a `'tray'` destination
          // never attempted a placement and naming one would be its own
          // small lie.
          const outcome = staged
            ? `"${item.title}" was staged, but the command did not finish.`
            : `Staging "${item.title}" failed.`
          const message = refreshed
            ? `${outcome} ${reason}`
            : `${outcome} ${reason} The latest plan could not be loaded — reload before trying again.`
          setFlash(message)
          return { error: message }
        }
        setStageRetry({
          label: item.title,
          // Dispatched through the ref, never through this render's own
          // binding: the click happens after the `refreshPlan()` above, so
          // capturing the function would replay the render that failed —
          // its `weeks`, its `firstFit`, its `api` — and place against the
          // very plan the coach was just told had been superseded (LG-457).
          // `item`/`destination`/`variables` stay captured on purpose:
          // replaying the same creation key is what makes this a retry
          // rather than a duplicate.
          run: () => runCatalogStageRef.current(item, destination, variables),
        })
        const message = refreshed
          ? `Staging "${item.title}" could not be confirmed. The latest plan was loaded; retry uses the same creation key.`
          : `Staging "${item.title}" did not finish. Retry uses the same creation key.`
        setFlash(message)
        return { error: message }
      }
    },
    [
      api,
      clientId,
      engagementId,
      firstFit,
      plan.engagement.clientElo,
      plan.engagement.pace,
      refreshPlan,
      weeks,
    ],
  )

  // Holds whatever the latest render built, so a stored retry re-resolves the
  // callback at click time instead of replaying a frozen one. Written in an
  // effect rather than during render: the only reader is a click handler, and
  // that always runs after a commit.
  const runCatalogStageRef = useRef(runCatalogStage)
  useEffect(() => {
    runCatalogStageRef.current = runCatalogStage
  })

  const stageItem = useCallback(
    (
      item: LibraryItem,
      // The two members of `runCatalogStage`'s own vocabulary this entry
      // point offers — derived from it, never borrowed from
      // `CascadeDestination`, which merely coincides with it today. The
      // cascade's next member would otherwise widen a library Add into
      // claiming the coach picked a day.
      destination: Extract<StageDestination, 'tray' | 'auto'>,
      sourceId: string | null = null,
    ): void => {
      if (stageInFlight.current) return
      if (item.alreadyAssigned) {
        setFlash(
          `"${item.title}" is already in this plan. Complete or cancel the existing assignment before adding it again.`,
        )
        return
      }
      if (!item.catalogKind || !item.catalogId) {
        setFlash(
          'This item has no persisted catalog identity and cannot be staged.',
        )
        return
      }
      const variables: Inputs['stage'] = {
        clientId,
        engagementId,
        creationKey: randomUuid(),
        subject: { kind: item.catalogKind, catalogId: item.catalogId },
        // Null for a plain add — the card wears the owning source at display
        // time. Non-null only when the coach picked a source on the row's
        // pinned title mark (LG-362 §3).
        sourceId,
        instructions: null,
        compatibilityDescription: null,
        completionMode: LEARNING_ITEM_COMPLETION_MODE[item.catalogKind],
        presentationMode: 'normal',
        preferredTemplatePolicy: 'auto',
        preferredTemplateId: null,
        targetCompanyId: null,
        repeatOfAssignmentId: null,
      }
      startStageOperation(() => runCatalogStage(item, destination, variables))
    },
    [clientId, engagementId, runCatalogStage, startStageOperation],
  )

  /**
   * Assigning a whole container (LG-907): one server call fans it out into a
   * card per member, then the board places them if the coach pressed Add.
   *
   * Placement is a loop rather than one call because `place` is per card, and
   * two pieces of state have to move with it: `committedByDay`, so the second
   * card is fitted against a day the first one just filled, and
   * `versionByWeek`, so the second placement into the same week presents the
   * version the first placement advanced it to rather than the one this render
   * loaded. Getting either wrong is not a cosmetic bug — the first over-fills
   * a day past its budget, which is exactly what refuses to publish; the
   * second CONFLICTs on the second card of every multi-card add.
   *
   * Running out of room stops the loop and leaves the remainder in the tray.
   * That is the same honest outcome a single Add already has when `firstFit`
   * finds nothing, and the flash says how many landed.
   */
  const runCascadeAssign = useCallback(
    async (
      container: Pick<ContainerRef, 'title'>,
      destination: CascadeDestination,
      variables: Inputs['cascadeAssign'],
    ): Promise<void> => {
      // Set the instant the fan-out commits, and read in the catch: a failure
      // in the placement loop below is a failed *placement* of cards that
      // already exist, and calling that "assigning failed" would contradict
      // the board the catch is about to render them onto.
      let assigned = false
      let placed = 0
      let alreadyPlaced = 0
      let notPlaceable = 0
      let noRoom = false
      try {
        const result = await api.cascadeAssign(variables)
        assigned = true
        setStageRetry(null)
        if (destination === 'auto') {
          const committedByDay = new Map<string, number>()
          const versionByWeek = new Map<string, number>()
          for (const card of result.created) {
            // A replay of a partly-committed batch hands back cards the lost
            // attempt already placed. Placing them again would move them off
            // the day the coach can already see them on — and counting them as
            // unplaced would report them as sitting in the tray.
            if (card.assignment.planDayId != null) {
              alreadyPlaced += 1
              continue
            }
            // Likewise a replayed card the client has since finished or the
            // coach cancelled: `place` refuses anything past `in_progress`, so
            // attempting it would turn a retry into a failed command.
            if (
              card.assignment.status !== 'planned' &&
              card.assignment.status !== 'in_progress'
            ) {
              notPlaceable += 1
              continue
            }
            // Once a search comes back empty the loop stops *searching* but
            // keeps classifying: breaking outright left every later card
            // uncounted, so a replayed card already sitting on a day was
            // reported as staged for want of room it never asked for.
            if (noRoom) continue
            const target = firstFit(card.effectiveMinutes, committedByDay)
            if (!target) {
              noRoom = true
              continue
            }
            const weekVersion =
              versionByWeek.get(target.week.id) ?? target.week.version
            const response = await api.place({
              clientId,
              engagementId,
              assignmentId: card.assignment.id,
              assignmentVersion: card.assignment.version,
              planDayId: target.dayId,
              weekId: target.week.id,
              weekVersion,
            })
            committedByDay.set(
              target.dayId,
              (committedByDay.get(target.dayId) ?? 0) + card.effectiveMinutes,
            )
            // The server is the authority on the new version; deriving it as
            // "+1" would encode a rule this component does not own.
            versionByWeek.set(response.week.id, response.week.version)
            placed += 1
          }
        }
        await refreshPlan()
        setFlash(
          cascadeOutcomeMessage(container.title, destination, {
            created: result.created.length,
            placed,
            alreadyPlaced,
            notPlaceable,
            noRoom,
            alreadyAssigned: result.alreadyAssigned,
          }),
        )
      } catch (error) {
        let refreshed = false
        try {
          await refreshPlan()
          refreshed = true
        } catch {
          // Retry remains explicit and replays the same batch key.
        }
        // Both branches drop any pending retry, for the same reason
        // `runCatalogStage` does: this attempt may itself be that retry, and
        // it just proved the replay cannot succeed.
        if (errorCode(error) === 'CONFLICT') {
          setStageRetry(null)
          setFlash(
            refreshed
              ? 'This plan changed in another session. The latest server state has been loaded; your command was not retried.'
              : 'This plan changed in another session, but the latest state could not be loaded. Reload before trying again.',
          )
          return
        }
        if (!isRetryableFailure(error)) {
          setStageRetry(null)
          const reason =
            errorReason(error) ?? 'The server rejected the command.'
          const outcome = assigned
            ? `"${container.title}" was assigned, but the command did not finish.`
            : `Assigning "${container.title}" failed.`
          setFlash(
            refreshed
              ? `${outcome} ${reason}`
              : `${outcome} ${reason} The latest plan could not be loaded — reload before trying again.`,
          )
          return
        }
        setStageRetry({
          label: container.title,
          // Through the ref for the same reason `runCatalogStage`'s retry is
          // (LG-457): the click happens after the refresh above, so a captured
          // callback would place against the plan the coach was just told had
          // been superseded. The variables stay captured on purpose — the same
          // batch key is what makes this a retry rather than a second assign.
          run: () =>
            runCascadeAssignRef.current(container, destination, variables),
        })
        setFlash(
          refreshed
            ? `Assigning "${container.title}" could not be confirmed. The latest plan was loaded; retry uses the same creation key.`
            : `Assigning "${container.title}" did not finish. Retry uses the same creation key.`,
        )
      }
    },
    [api, clientId, engagementId, firstFit, refreshPlan],
  )

  const runCascadeAssignRef = useRef(runCascadeAssign)
  useEffect(() => {
    runCascadeAssignRef.current = runCascadeAssign
  })

  const cascadeContainer = useCallback(
    (container: ContainerRef, destination: CascadeDestination): void => {
      if (stageInFlight.current) return
      const variables: Inputs['cascadeAssign'] = {
        clientId,
        engagementId,
        // One key for the batch; the server derives each member's own from it,
        // so Retry replays the cards the first attempt made.
        creationKey: randomUuid(),
        sourceContentId: container.sourceContentId,
        targetCompanyId: null,
      }
      startStageOperation(() =>
        runCascadeAssign(container, destination, variables),
      )
    },
    [clientId, engagementId, runCascadeAssign, startStageOperation],
  )

  const cardIdentity = useCallback(
    (card: VersionedCard) => ({
      clientId,
      engagementId,
      assignmentId: card.id,
      assignmentVersion: card.version,
      planDayId: card.planDayId ?? null,
      weekId: card.weekId ?? null,
      weekVersion: card.weekVersion ?? null,
    }),
    [clientId, engagementId],
  )

  /**
   * The four fields every assignment write shares.
   *
   * Separate from `cardIdentity` because the procedure inputs are `.strict()`
   * and only some of them take the schedule triple: a write that does not
   * touch scheduling refuses `planDayId`/`weekId`/`weekVersion` outright.
   * Spreading the wider object into a narrower schema type-checks — excess-
   * property checking does not survive a spread — and then fails at the
   * boundary, which is how the coach note and the completion toggle shipped
   * broken.
   */
  const assignmentIdentity = useCallback(
    (card: VersionedCard) => ({
      clientId,
      engagementId,
      assignmentId: card.id,
      assignmentVersion: card.version,
    }),
    [clientId, engagementId],
  )

  /**
   * Finds a card anywhere on the board and proves it is an assignment. The two
   * details writes search tray + every day, and that list also holds calendar
   * overlays, so the version guard is what keeps a synced event out of a write
   * path — see `withVersion`.
   */
  const findAssignmentCard = useCallback(
    (cardId: string): VersionedCard | null =>
      withVersion(
        [
          ...tray,
          ...weeks.flatMap((week) => week.days.flatMap((day) => day.cards)),
        ].find((candidate) => candidate.id === cardId),
      ),
    [tray, weeks],
  )

  const addCustom = useCallback(
    async (
      card: CustomCardInput,
      // The same vocabulary `runCatalogStage` takes, rather than a second one
      // that spells "tray" as `null`: the two paths mirror each other guard
      // for guard, and `typeof null === 'object'` makes divergent spellings
      // exactly the kind that break silently when someone later unifies them.
      destination: StageDestination,
      variables: Inputs['stage'],
    ): Promise<void> => {
      // Resolved here rather than at the call site, because a retry runs
      // after the catch's `refreshPlan()` and has to answer "where does this
      // go?" against the board that refresh loaded, not the one that failed
      // (LG-457).
      //
      // `'auto'` stays a *token*, exactly as it does on the catalog path.
      // `addInterviewTier`'s auto placement used to pre-resolve a day with
      // `firstFit` and hand the result in, but a machine-picked day is not
      // the coach's choice: replaying it re-places onto a day a colleague may
      // have filled in the meantime, producing the over-budget day that makes
      // a week unpublishable. Re-fitting is the only honest replay.
      //
      // An explicit `{ weekId, dayId }` *is* the coach's choice, so it is
      // re-found, never re-picked. Only the week's `version` comes from the
      // fresh board — that is an optimistic-concurrency token, not part of
      // what they chose, and replaying the failed attempt's copy is a
      // guaranteed CONFLICT.
      const picked =
        destination === 'auto'
          ? (() => {
              const fit = firstFit(card.estimatedMinutes)
              return fit === null
                ? null
                : { weekId: fit.week.id, dayId: fit.dayId }
            })()
          : typeof destination === 'object'
            ? destination
            : null
      const chosenWeek =
        picked === null
          ? null
          : (weeks.find((week) => week.id === picked.weekId) ?? null)
      const chosenDay =
        picked === null
          ? null
          : (chosenWeek?.days.find((day) => day.id === picked.dayId) ?? null)
      // Mirrors `runCatalogStage`'s guard of the same name. An explicit pick
      // that no longer resolves is a failure to report, not a placement to
      // skip under a "was saved to the canonical plan" flash — that is the
      // fabricated success this file's `RELATED_WRITE_NOT_BUILT` doctrine
      // exists to reject. `'auto'` finding no room is a different, honest
      // outcome: the coach never committed to one specific day there.
      const pickedDayUnavailable =
        destination === 'picked-day-gone' ||
        (typeof destination === 'object' &&
          (chosenWeek === null ||
            chosenWeek.version === undefined ||
            chosenDay === null))
      // As in `runCatalogStage`: `api.setEstimate` and `api.place` below share
      // this `try`, so a failure after the stage commits is a failed follow-up
      // on a card that now exists, not a failed save.
      let created = false
      try {
        const staged = await api.stage(variables)
        created = true
        setStageRetry(null)
        const estimatedMinutes = card.estimatedMinutes
        let assignment: {
          id: string
          version: number
          estimatedMinutes: number | null
          planDayId: string | null | undefined
        } = staged.assignment
        if (
          assignment.planDayId == null &&
          assignment.estimatedMinutes !== estimatedMinutes
        ) {
          assignment = await api.setEstimate({
            clientId,
            engagementId,
            assignmentId: assignment.id,
            assignmentVersion: assignment.version,
            estimatedMinutes,
          })
        }
        // Explicit `!== undefined`, not truthiness: `version` and `id` are a
        // number and a string, and a falsy-but-valid one would skip the
        // placement silently under the success flash below. The same fix
        // round closed this gap in `runCatalogStage` and
        // `replaceWithRelatedProblem`; this was the third call site.
        // Where the card actually ended up, which is not the same question as
        // where it was headed. A creation-key replay whose original placement
        // committed before its response was lost comes back already on a day,
        // so this starts true and no placement is attempted.
        let placed = assignment.planDayId != null
        if (
          !placed &&
          chosenWeek !== null &&
          chosenWeek.version !== undefined &&
          chosenDay !== null &&
          chosenDay.id !== undefined
        ) {
          await api.place({
            clientId,
            engagementId,
            assignmentId: assignment.id,
            assignmentVersion: assignment.version,
            planDayId: chosenDay.id,
            weekId: chosenWeek.id,
            weekVersion: chosenWeek.version,
          })
          placed = true
        }
        setStageRetry(null)
        await refreshPlan()
        // Reported from the outcome, never from the pre-stage resolution —
        // `runCatalogStage` pairs its own `pickedDayUnavailable` with `!placed`
        // for exactly this reason. The retry above can land the card on the
        // picked day *while* that day has dropped off the paged-in board, and
        // telling the coach to "place it from the tray" then sends them
        // hunting an empty tray for a card that is already scheduled.
        setFlash(
          pickedDayUnavailable && !placed
            ? `"${card.title}" was saved to the staging tray, but the day you picked is no longer available. Place it from the tray.`
            : placed
              ? `"${card.title}" was saved to the canonical plan.`
              : destination === 'tray'
                ? `"${card.title}" was saved to the staging tray.`
                : `"${card.title}" was saved to the staging tray because no available day had room.`,
        )
      } catch (error) {
        let refreshed = false
        try {
          await refreshPlan()
          refreshed = true
        } catch {
          // Retry remains explicit and resumes from the creation-key replay.
        }
        // As on the catalog stage path, both branches drop any pending retry:
        // this attempt may be that retry, and it just proved the replay
        // cannot succeed.
        if (errorCode(error) === 'CONFLICT') {
          setStageRetry(null)
          setFlash(
            refreshed
              ? 'This plan changed in another session. The latest server state has been loaded; your command was not retried.'
              : 'This plan changed in another session, but the latest state could not be loaded. Reload before trying again.',
          )
          return
        }
        // A rejected command returns the identical error on every replay, so
        // it gets the reason instead of a retry button.
        if (!isRetryableFailure(error)) {
          setStageRetry(null)
          const reason =
            errorReason(error) ?? 'The server rejected the command.'
          const outcome = created
            ? `"${card.title}" was saved, but the follow-up did not finish.`
            : `Saving "${card.title}" failed.`
          setFlash(
            refreshed
              ? `${outcome} ${reason}`
              : `${outcome} ${reason} The latest plan could not be loaded — reload before trying again.`,
          )
          return
        }
        setStageRetry({
          label: card.title,
          // Through the ref for the same reason `runCatalogStage`'s retry is
          // (LG-457) — only the callback re-resolves; the creation key in
          // `variables` stays captured.
          run: () => addCustomRef.current(card, destination, variables),
        })
        setFlash(
          refreshed
            ? `Saving "${card.title}" could not be confirmed. The latest plan was loaded; retry uses the same creation key.`
            : `Saving "${card.title}" did not finish. Retry uses the same creation key.`,
        )
      }
    },
    [api, clientId, engagementId, firstFit, refreshPlan, weeks],
  )

  // Same latest-callback ref as `runCatalogStageRef`, for the same retry.
  const addCustomRef = useRef(addCustom)
  useEffect(() => {
    addCustomRef.current = addCustom
  })

  const bulkVersionState = useCallback(() => {
    const now = new Date()
    const eligibleWeeks = plan.weeks.filter((week) =>
      isNonPastDraftWeek(week, plan.engagement.timeZone, now),
    )
    const eligibleWeekIds = new Set(eligibleWeeks.map((week) => week.id))
    const eligibleDayIds = new Set(
      plan.days
        .filter((day) => eligibleWeekIds.has(day.weekId))
        .map((day) => day.id),
    )
    const scheduled = plan.assignments.filter(
      (assignment) =>
        assignment.planDayId !== null &&
        eligibleDayIds.has(assignment.planDayId) &&
        assignment.status !== 'cancelled',
    )
    const trayIds = new Set(plan.trayAssignmentIds)
    const actionableTray = plan.assignments.filter(
      (assignment) =>
        trayIds.has(assignment.id) &&
        assignment.planDayId === null &&
        ['planned', 'in_progress'].includes(assignment.status),
    )
    const versions = (assignments: typeof plan.assignments) =>
      assignments.map((assignment) => ({
        assignmentId: assignment.id,
        version: assignment.version,
      }))
    return {
      weekVersions: eligibleWeeks.map((week) => ({
        weekId: week.id,
        version: week.version,
      })),
      scheduledVersions: versions(scheduled),
      trayVersions: versions(actionableTray),
    }
  }, [plan])

  const distributeInput = useCallback((): Inputs['distribute'] => {
    const state = bulkVersionState()
    return {
      clientId,
      engagementId,
      weekVersions: state.weekVersions,
      assignmentVersions: [...state.scheduledVersions, ...state.trayVersions],
    }
  }, [bulkVersionState, clientId, engagementId])

  const optimizeInput = useCallback((): Inputs['optimize'] => {
    const state = bulkVersionState()
    return {
      clientId,
      engagementId,
      weekVersions: state.weekVersions,
      assignmentVersions: state.scheduledVersions,
    }
  }, [bulkVersionState, clientId, engagementId])

  const coverage = api.coverage ?? { plan: false, tray: false }
  const bulkActionsReady = coverage.plan && coverage.tray
  const addWeekReady = (coverage.addWeek ?? coverage.plan) && !weekShapePending

  const bulkActionsReason = bulkActionsReady
    ? null
    : 'Load the complete plan and every tray page before running a bulk command.'

  return useMemo<CurriculumSlice>(
    () => ({
      persisted: true,
      flash,
      dismissFlash: () => setFlash(null),
      weeks,
      tray,
      filters,
      sorts,
      typeFilter,
      companyTags,
      hiddenCols,
      columnOrder,
      seenFilter,
      // The pill's filter must not apply before the overlay has arrived —
      // with no map, "unseen only" would briefly hide nothing and then
      // reshuffle; worse, it cannot tell seen rows apart at all yet.
      seenOverlayPending: seenByProblemId === undefined,
      // A dead query means the suspension above never lifts — the pill has
      // to disclose that instead of claiming a filter that is not running.
      seenOverlayFailed:
        seenByProblemId === undefined && api.seenSummary.error != null,
      libraryTab,
      sourceTreeOpen,
      setSourceTreeOpen,
      placing,
      activePresetId: null,
      libraryItems: items,
      assignedLearningItemIds: activeLearningItemIds,
      planOptions: api.planOptions,
      sourceOptions: api.sourceOptions,
      browseStreaming: api.browse.isStreaming,
      // The drain stopped short: a page failed, or it hit the hook's page cap.
      // What is on screen is a PREFIX of the library, so every filter, sort and
      // search below is answering a question about the wrong set — the exact
      // failure this work exists to remove. The consumer must say so next to
      // the count instead of letting a partial corpus read as the whole one.
      browseIncomplete: api.browse.isTruncated,
      browseRefreshing: api.browse.isRefreshing,
      planHasMore: !coverage.plan,
      planLoadingMore: api.isLoadingMorePlan,
      loadMorePlan: () => {
        void api.loadMorePlan().catch(() => {
          setFlash('More plan dates could not be loaded.')
        })
      },
      planHistoryHasMore: api.planHistoryHasMore,
      loadEarlierPlan: () => {
        void api.loadEarlierPlan().catch(() => {
          setFlash('Earlier plan dates could not be loaded.')
        })
      },
      trayHasMore: !coverage.tray,
      trayLoadingMore: api.isLoadingMoreTray,
      loadMoreTray: () => {
        void api.loadMoreTray().catch(() => {
          setFlash('More staged assignments could not be loaded.')
        })
      },
      stagePending,
      clientElo: plan.engagement.clientElo,
      optimalEloBand: plan.engagement.optimalEloBand,
      pace: plan.engagement.pace,
      setPace: (pace) => {
        setPacePending(true)
        void mutation(
          () =>
            api.setPace({
              clientId,
              engagementId,
              engagementVersion: plan.engagement.version,
              pace,
            }),
          `Pace set to ${pace}. Estimates updated.`,
          { clientVisible: true },
        ).finally(() => setPacePending(false))
      },
      pacePending,
      targetCompanies: plan.targetCompanies.map((company) => ({
        id: company.companyId,
        name: company.companyName,
      })),
      retryStage: stageRetry
        ? () => startStageOperation(stageRetry.run)
        : undefined,
      retryStageLabel: stageRetry?.label ?? null,
      bulkActionsReady,
      bulkActionsReason,
      addWeekReady,
      addWeekReason: addWeekReady
        ? null
        : weekShapePending
          ? WEEK_COMMAND_BUSY_MESSAGE
          : 'Load the complete plan and latest prior week before adding a week.',
      addAuto: (item, sourceId) => stageItem(item, 'auto', sourceId),
      stageToTray: (item, sourceId) => stageItem(item, 'tray', sourceId),
      // Containers take a different command from items: assigning one fans it
      // out into a card per member (LG-907), so there is nothing here to pass
      // through `stageItem`'s single-subject shape.
      assignContainer: cascadeContainer,
      removeFromTray: (cardId) => {
        const card = withVersion(
          tray.find((candidate) => candidate.id === cardId),
        )
        if (!card || removingIds.includes(cardId)) return
        setRemovingIds((current) => [...current, cardId])
        void mutation(
          () =>
            api.cancel({
              ...cardIdentity(card),
              commandId: randomUuid(),
            }),
          `"${card.title}" removed from the staging tray.`,
          { clientVisible: true },
        ).finally(() =>
          setRemovingIds((current) => current.filter((id) => id !== cardId)),
        )
      },
      removingIds,
      togglePlacing: (cardId) => {
        const card = tray.find((candidate) => candidate.id === cardId)
        if (!card) return
        setPlacingId((current) => (current === cardId ? null : cardId))
      },
      cancelPlacing: () => setPlacingId(null),
      placeAt: (weekIdx, dayIdx) => {
        const card = tray.find((candidate) => candidate.id === placingId)
        const week = weeks[weekIdx]
        const day = week?.days[dayIdx]
        if (!card || !week || !day?.id || !week.version) return
        void mutation(
          () =>
            api.place({
              clientId,
              engagementId,
              assignmentId: card.id,
              assignmentVersion: card.version!,
              planDayId: day.id!,
              weekId: week.id,
              weekVersion: week.version!,
            }),
          `"${card.title}" placed on ${day.label}.`,
          { apply: (result) => api.applyPlacementResult(result) },
        )
        setPlacingId(null)
      },
      placeCardAt: (cardId, weekIdx, dayIdx) => {
        const card = tray.find((candidate) => candidate.id === cardId)
        const week = weeks[weekIdx]
        const day = week?.days[dayIdx]
        if (!card || !week || !day?.id || !week.version) return
        // Land the drop in the plan cache synchronously (with the card's
        // current — soon stale — version) so @dnd-kit's drop animation
        // measures the card already in its destination day instead of flying
        // the overlay back to the tray. The mutation's returned data
        // re-applies the authoritative versions; the failure path's full
        // refetch discards this patch.
        api.applyPlacementResult({
          assignment: {
            id: card.id,
            planDayId: day.id,
            version: card.version!,
          },
          week: null,
        })
        void mutation(
          () =>
            api.place({
              clientId,
              engagementId,
              assignmentId: card.id,
              assignmentVersion: card.version!,
              planDayId: day.id!,
              weekId: week.id,
              weekVersion: week.version!,
            }),
          `"${card.title}" placed on ${day.label}.`,
          { apply: (result) => api.applyPlacementResult(result) },
        )
        setPlacingId((current) => (current === card.id ? null : current))
      },
      removeFromDay: (cardId) => {
        const card = weeks
          .flatMap((week) => week.days.flatMap((day) => day.cards))
          .find((candidate) => candidate.id === cardId)
        if (!card || !card.planDayId || !card.weekId || !card.weekVersion)
          return
        void mutation(
          () =>
            api.unplace({
              clientId,
              engagementId,
              assignmentId: card.id,
              assignmentVersion: card.version!,
              planDayId: card.planDayId!,
              weekId: card.weekId!,
              weekVersion: card.weekVersion!,
            }),
          `"${card.title}" moved to the staging tray.`,
          { apply: (result) => api.applyPlacementResult(result) },
        )
      },
      moveCardTo: (cardId, weekIdx, dayIdx) => {
        const card = weeks
          .flatMap((week) => week.days.flatMap((day) => day.cards))
          .find((candidate) => candidate.id === cardId)
        const targetWeek = weeks[weekIdx]
        const targetDay = targetWeek?.days[dayIdx]
        if (
          !card ||
          card.done ||
          card.fixed ||
          !card.version ||
          !targetWeek?.version ||
          !targetDay?.id
        ) {
          return
        }
        // Dropping a card back on the day it already occupies is a no-op the
        // server would happily accept — but `applyPlanAssignment` re-appends
        // the row to the end of `plan.assignments`, and day columns take their
        // card order straight from that array. The card would visibly jump to
        // the bottom of its own column under a "moved" flash, then jump back
        // on the next refetch. Drop it silently instead.
        if (targetDay.cards.some((candidate) => candidate.id === cardId)) {
          return
        }
        // A move into another week has to name the week the card is leaving so
        // the server can lock and version-check it (LG-360 §4). The card
        // carries both, but a card the board is still hydrating may not — and
        // sending a move with no source would be read as "already in the target
        // week" and refused, so hold it rather than issue a doomed write.
        // Compared case-insensitively, matching the service: reading one week
        // spelled two ways as two would make an ordinary same-week drag demand
        // a version the card might not carry yet, and send a source the server
        // would only have to collapse again.
        const crossWeek =
          card.weekId?.toLowerCase() !== targetWeek.id.toLowerCase()
        if (crossWeek && (!card.weekId || !card.weekVersion)) {
          setFlash(
            `"${card.title}" is still loading its week — try that move again in a moment.`,
          )
          return
        }
        // Same optimistic cache-land as placeCardAt: the drop animation
        // glides the overlay into the target day instead of back to the
        // source; server data then patches the real versions.
        api.applyPlacementResult({
          assignment: {
            id: card.id,
            planDayId: targetDay.id,
            version: card.version,
          },
          week: null,
        })
        void mutation(
          () =>
            api.move({
              clientId,
              engagementId,
              assignmentId: card.id,
              assignmentVersion: card.version!,
              planDayId: targetDay.id!,
              weekId: targetWeek.id,
              weekVersion: targetWeek.version!,
              // Omitted for a same-week move, where the service derives the
              // source from the target and applies the guard it always has.
              ...(crossWeek && card.weekId && card.weekVersion
                ? {
                    source: {
                      weekId: card.weekId,
                      weekVersion: card.weekVersion,
                    },
                  }
                : {}),
            }),
          `"${card.title}" moved to ${targetDay.label}.`,
          { apply: (result) => api.applyPlacementResult(result) },
        )
      },
      distributeTray: () => {
        if (!bulkActionsReady) {
          setFlash(bulkActionsReason)
          return
        }
        void mutation(
          () => api.distribute(distributeInput()),
          'Tray assignments were distributed by server-side hour budgets.',
        )
      },
      optimizeSchedule: () => {
        if (!bulkActionsReady) {
          setFlash(bulkActionsReason)
          return
        }
        void mutation(
          () => api.optimize(optimizeInput()),
          'Draft assignments were optimized by the server.',
        )
      },
      publishWeek: (weekId) => {
        const week = weeks.find((candidate) => candidate.id === weekId)
        if (!week?.version) return
        // Both mutations of a double-click carry the `weekVersion` the board
        // was rendering. The server bumps it on the first, so the second
        // fails its version check — an immediate CONFLICT for what the coach
        // experienced as one impatient click.
        if (weekShapeInFlight.current) {
          setFlash(WEEK_COMMAND_BUSY_MESSAGE)
          return
        }
        weekShapeInFlight.current = true
        setWeekShapePending(true)
        void mutation(
          () =>
            api.publishWeek({
              clientId,
              engagementId,
              weekId,
              weekVersion: week.version!,
            }),
          `${week.label} published — the client can now see it.`,
          { clientVisible: true },
        ).finally(() => {
          weekShapeInFlight.current = false
          setWeekShapePending(false)
        })
      },
      unpublishWeek: (weekId) => {
        const week = weeks.find((candidate) => candidate.id === weekId)
        if (!week?.version) return
        // The board says the same thing the service does before spending a
        // round trip on it — and says it in a sentence, where the service
        // has only PRECONDITION_FAILED to report with.
        const blocked = unpublishBlockedReason(week)
        if (blocked) {
          setFlash(blocked)
          return
        }
        // Same version race as `publishWeek`; matching its sibling rather
        // than diverging.
        if (weekShapeInFlight.current) {
          setFlash(WEEK_COMMAND_BUSY_MESSAGE)
          return
        }
        weekShapeInFlight.current = true
        setWeekShapePending(true)
        void mutation(
          () =>
            api.unpublishWeek({
              clientId,
              engagementId,
              weekId,
              weekVersion: week.version!,
            }),
          `${week.label} unpublished — it is a draft again and hidden from the client.`,
          { clientVisible: true },
        ).finally(() => {
          weekShapeInFlight.current = false
          setWeekShapePending(false)
        })
      },
      deleteWeek: (weekId) => {
        const week = weeks.find((candidate) => candidate.id === weekId)
        if (!week?.version) return
        if (weekShapeInFlight.current) {
          setFlash(WEEK_COMMAND_BUSY_MESSAGE)
          return
        }
        weekShapeInFlight.current = true
        setWeekShapePending(true)
        // Landed from the result, like addWeek (LG-543). The delete control is
        // rendered for every *merged* week — including ones that live only in
        // an extra range page — so `applyWeekDeleted` patches all the merged
        // pages rather than only the cache page `setData` targets; patching
        // just that one would append tray ids for rows it never unplaced, and
        // the cards would vanish from both the board and the tray under a
        // success flash.
        void mutation(
          () =>
            api.deleteWeek({
              clientId,
              engagementId,
              weekId,
              weekVersion: week.version!,
            }),
          `${week.label} deleted — active cards moved to the tray.`,
          { apply: (result) => api.applyWeekDeleted(result) },
        ).finally(() => {
          weekShapeInFlight.current = false
          setWeekShapePending(false)
        })
      },
      addWeek: () => {
        if (!addWeekReady) {
          setFlash(
            'Load the complete plan and latest prior week before adding a week.',
          )
          return
        }
        // Without this, a double-click fires two mutations that both name the
        // same week as `previousWeekId`. By the time the second lands the
        // server's latest week is the one the first just inserted, so it
        // fails the `previousWeekId !== latest.id` check — an immediate
        // CONFLICT for what the coach experienced as one impatient click.
        if (weekShapeInFlight.current) {
          setFlash(WEEK_COMMAND_BUSY_MESSAGE)
          return
        }
        const previous = plan.weeks.reduce<Plan['weeks'][number] | null>(
          (latest, week) =>
            latest === null || week.startsOn > latest.startsOn ? week : latest,
          null,
        )
        weekShapeInFlight.current = true
        setWeekShapePending(true)
        void mutation(
          () =>
            api.addWeek({
              clientId,
              engagementId,
              previousWeekId: previous?.id ?? null,
              previousWeekVersion: previous?.version ?? null,
            }),
          'A new week was added with carried-forward budgets.',
          { apply: (result) => api.applyWeekAdded(result) },
        ).finally(() => {
          weekShapeInFlight.current = false
          setWeekShapePending(false)
        })
      },
      setBudget: (weekIdx, dayIdx, budget) => {
        const week = weeks[weekIdx]
        const day = week?.days[dayIdx]
        if (!week?.version || !day?.id) return
        void mutation(
          () =>
            api.setBudget({
              clientId,
              engagementId,
              weekId: week.id,
              weekVersion: week.version!,
              planDayId: day.id!,
              budgetMinutes: Math.max(
                0,
                Math.min(480, Math.round(budget * 60)),
              ),
            }),
          `${day.label} budget updated.`,
        )
      },
      toggleWeek: (weekId) => {
        const week = serverState.weeks.find(
          (candidate) => candidate.id === weekId,
        )
        if (!week) return
        const folded = foldedWeek(week, weekFolds)
        setWeekOpenOverrides((current) => {
          // Previous state is the only input to the flip. Reading the already
          // rendered `open` instead would make two toggles batched into one
          // commit net to a single toggle rather than to none — `week` here
          // contributes only `folded`, which this action cannot change.
          const open = current.get(weekId) ?? defaultWeekOpen({ folded })
          return new Map(current).set(weekId, !open)
        })
      },
      toggleWeekFold: (weekId) => {
        const week = serverState.weeks.find(
          (candidate) => candidate.id === weekId,
        )
        if (!week) return
        // Previous state, not the rendered map, for the same reason
        // `toggleWeek` takes it from the updater: two toggles batched into one
        // commit have to net to nothing. Weeks that may not be folded are
        // refused inside `withWeekFoldToggled`.
        setWeekFolds((current) => withWeekFoldToggled(current, week))
      },
      addCustomCard: (weekIdx, dayIdx, card) => {
        if (stageInFlight.current) return
        const week = weeks[weekIdx]
        const day = week?.days[dayIdx]
        // `day.id` is optional on the board type (the mock slice omits it),
        // and it is the identity `addCustom` now places by — an id-less day
        // has nothing to place against, so refuse here rather than stage a
        // card that can never land.
        //
        // Says so rather than returning silently: the coach has just filled in
        // the dialog and pressed "Add card", and a bare `return` gives them
        // neither a card nor a reason. Same sentence `replaceWithRelatedProblem`
        // gives for the identical condition.
        if (!week || !day || day.id === undefined) {
          setFlash(
            'That day is no longer available. Reopen the board to see the current plan.',
          )
          return
        }
        // Captured into plain `const`s rather than read off `week`/`day`
        // inside the closure below, for the reason
        // `replaceWithRelatedProblem` spells out: narrowing a property
        // access does not reliably survive into a callback.
        const weekId = week.id
        const dayId = day.id
        const variables: Inputs['stage'] = {
          clientId,
          engagementId,
          creationKey: randomUuid(),
          subject: {
            compatibilityType: card.type === 'other' ? 'OTHER' : 'TASK',
            coachTitle: card.title,
            clientTitle: card.title,
            customUrl: card.link ?? null,
            track: card.type,
          },
          // One-off card: no catalog problem behind it, so no source.
          sourceId: null,
          instructions: card.instructions ?? null,
          compatibilityDescription: null,
          completionMode: 'manual',
          presentationMode: 'normal',
          preferredTemplatePolicy: 'none',
          preferredTemplateId: null,
          targetCompanyId: null,
          repeatOfAssignmentId: null,
        }
        // By id, not by the indices above: the retry `addCustom` may store
        // outlives this render's `weeks` (LG-457).
        startStageOperation(() => addCustom(card, { weekId, dayId }, variables))
      },
      convertToAiMock: (cardId) => {
        const card = withVersion(
          weeks
            .flatMap((week) => week.days.flatMap((day) => day.cards))
            .find((candidate) => candidate.id === cardId),
        )
        if (!card) return
        void mutation(
          () => api.convertAiMock(cardIdentity(card)),
          `"${card.title}" converted to an AI mock.`,
        )
      },
      revertAiMock: (cardId) => {
        const card = withVersion(
          weeks
            .flatMap((week) => week.days.flatMap((day) => day.cards))
            .find((candidate) => candidate.id === cardId),
        )
        if (!card) return
        void mutation(
          () => api.revertAiMock(cardIdentity(card)),
          `"${card.title}" reverted to a normal assignment.`,
        )
      },
      linkAiMockQuestion: (cardId, item) => {
        const card = withVersion(
          weeks
            .flatMap((week) => week.days.flatMap((day) => day.cards))
            .find((candidate) => candidate.id === cardId),
        )
        if (!card) return
        void mutation(
          () =>
            api.linkAiMock({
              ...cardIdentity(card),
              learningItemId: item.learningItemId ?? item.id,
            }),
          `AI mock linked to "${item.title}".`,
        )
      },
      addInterviewTier: (tier, destination) => {
        if (stageInFlight.current) return
        const title =
          tier === 'ai'
            ? 'AI mock interview'
            : tier === 'peer'
              ? 'Schedule 2 peer mocks'
              : 'Book a coach session'
        const card: CustomCardInput = {
          title,
          type: 'interview',
          meta: 'Interview practice',
          estimatedMinutes: tier === 'ai' ? 60 : 30,
        }
        const variables: Inputs['stage'] = {
          clientId,
          engagementId,
          creationKey: randomUuid(),
          subject: {
            compatibilityType: tier === 'ai' ? 'OTHER' : 'TASK',
            coachTitle: title,
            clientTitle: title,
            customUrl: null,
            track: 'interview',
          },
          // One-off card: no catalog problem behind it, so no source.
          sourceId: null,
          instructions: null,
          compatibilityDescription: null,
          completionMode: 'manual',
          presentationMode: tier === 'ai' ? 'ai_mock_hidden' : 'normal',
          preferredTemplatePolicy: 'none',
          preferredTemplateId: null,
          targetCompanyId: null,
          repeatOfAssignmentId: null,
        }
        // `'auto'` travels as the intent it is, rather than a day resolved
        // here: `firstFit` runs inside `addCustom`, so a retry re-fits
        // against the board the catch's refresh loaded instead of replaying
        // a day a colleague may since have filled (LG-457).
        startStageOperation(() => addCustom(card, destination, variables))
      },
      applyPreset: () =>
        setFlash('Presets are not available for persisted curriculum data.'),
      setFilters,
      setSorts,
      setTypeFilter,
      cycleCompanyTag: (company) =>
        setCompanyTags((current) => {
          const existing = current.find((tag) => tag.company === company)
          if (!existing) return [...current, { company, mode: 'include' }]
          if (existing.mode === 'include')
            return current.map((tag) =>
              tag.company === company ? { ...tag, mode: 'exclude' } : tag,
            )
          return current.filter((tag) => tag.company !== company)
        }),
      cycleSeenFilter: () => {
        // Side effect in the handler, never the updater (updaters must stay
        // pure — StrictMode replays them); `seenFilter` is a dep of this
        // memo, so the closure is never stale.
        const next = nextSeenFilter(seenFilter)
        writeStoredSeenFilter(next)
        setSeenFilter(next)
      },
      toggleColumn: (column) =>
        setHiddenCols((current) =>
          current.includes(column)
            ? current.filter((candidate) => candidate !== column)
            : [...current, column],
        ),
      setColumnOrder,
      setLibraryTab,
      setAssignmentEstimate: (cardId, estimatedMinutes) => {
        const card = withVersion(
          tray.find((candidate) => candidate.id === cardId),
        )
        if (!card) return Promise.resolve(false)
        return mutation(
          () =>
            api.setEstimate({
              clientId,
              engagementId,
              assignmentId: card.id,
              assignmentVersion: card.version,
              estimatedMinutes,
            }),
          estimatedMinutes === null
            ? `"${card.title}" estimate reset to computed.`
            : `"${card.title}" estimate updated.`,
        )
      },
      setAssignmentTargetCompany: (cardId, targetCompanyId) => {
        const card = findAssignmentCard(cardId)
        if (!card) return
        void mutation(
          () =>
            api.setTargetCompany({
              ...cardIdentity(card),
              targetCompanyId,
            }),
          `"${card.title}" target company updated.`,
        )
      },
      saveCoachNote: ({ cardId, note }) => {
        const card = findAssignmentCard(cardId)
        if (!card) {
          return Promise.resolve({
            error: 'This card is no longer on the board.',
          })
        }
        return detailWrite(
          () => api.setCoachNote({ ...assignmentIdentity(card), note }),
          'This card changed in another session. Close and reopen it to see the current note.',
        )
      },
      saveUniversalNotes: ({ learningItemId, notes, expectedNotes }) =>
        detailWrite(
          // Deliberately no assignment identity: these notes hang off the
          // catalog item, not this engagement's card, and the procedure's
          // input is `.strict()`.
          () => api.setUniversalNotes({ learningItemId, notes, expectedNotes }),
          'Someone else edited these notes first. Close and reopen the card to see their version before writing over it.',
        ),
      setAssignmentCompletion: async ({ cardId, target }) => {
        const card = findAssignmentCard(cardId)
        if (!card) {
          return { error: 'This card is no longer on the board.' }
        }
        const result = await detailWrite(
          () =>
            api.completeManual({
              ...assignmentIdentity(card),
              commandId: randomUuid(),
              target,
            }),
          'This card changed in another session. Close and reopen it to see its current state.',
        )
        // Only on a write that landed: a refused tick left the review exactly
        // where it was, and re-reading it would flash the section through
        // `pending` for nothing.
        // On every outcome, not just success. `detailWrite`'s retryable branch
        // reports "could not confirm the save" for a write whose commit is
        // genuinely unknown — it resyncs the board there for exactly that
        // reason — so skipping the reload on a non-null error would leave the
        // Review section stale through the one path that is ambiguous rather
        // than refused. A reload after a truly refused tick costs one read and
        // shows nothing: the section keeps its current answer while it runs.
        setReviewReload((previous) => ({
          assignmentId: cardId,
          seq: (previous?.seq ?? 0) + 1,
        }))
        return result
      },
      setDetailsProblemId,
      cardDetails,
      cardDetailsPending,
      cardDetailsError,
      setDetailsAssignmentId,
      cardReview,
      saveSource: ({ cardId, sourceId }) => {
        const card = findAssignmentCard(cardId)
        if (!card) {
          return Promise.resolve({
            error: 'This card is no longer on the board.',
          })
        }
        return detailWrite(
          // `assignmentIdentity`, not `cardIdentity` — `setSourceInput` is
          // `.strict()` over the four-field identity and would refuse the
          // schedule triple, which is exactly the Task 0 bug.
          () => api.setSource({ ...assignmentIdentity(card), sourceId }),
          'This card changed in another session. Close and reopen it to see which source it is assigned from.',
        )
      },
      // Same write as saveSource, reported through the board flash instead of
      // a dialog: the inline switcher lives on the board, where staging
      // failures already land. `detailWrite`'s refresh-on-either-outcome is
      // exactly right here too — the switched card's mark comes from the
      // refetched plan.
      switchCardSource: async ({ cardId, sourceId }) => {
        const card = findAssignmentCard(cardId)
        if (!card) {
          setFlash('This card is no longer on the board.')
          return
        }
        const result = await detailWrite(
          () => api.setSource({ ...assignmentIdentity(card), sourceId }),
          'This card changed in another session. The board has been refreshed — check which source it wears now.',
        )
        if (result.error !== null) setFlash(result.error)
      },
      // "Add to the staging tray" / "Assign in addition" (LG-360 §8) — stage
      // then, for a picked day, place, exactly the two calls `runCatalogStage`
      // already makes for a plain library add, down to the creation key that
      // makes a retry replay rather than duplicate. Reusing it here (with its
      // `destination` generalised to accept a picked day, not just
      // `'tray'`/`'auto'`) is the whole point — a second copy of its retry
      // and conflict handling is exactly what this doesn't do.
      stageRelatedProblem: (input) => {
        if (activeLearningItemIds.has(input.learningItemId)) {
          // `CoachingAssignment_engagement_learningItem_active_uidx` would
          // reject this server-side; saying so first is kinder than a
          // constraint violation.
          return Promise.resolve({
            error: `"${input.title}" is already in this plan. Complete or cancel the existing assignment before adding it again.`,
          })
        }
        const variables: Inputs['stage'] = {
          clientId,
          engagementId,
          creationKey: randomUuid(),
          subject: { kind: 'problem', catalogId: input.catalogId },
          // A related-problem add takes the item's owning source, same as a
          // plain library add.
          sourceId: null,
          instructions: null,
          compatibilityDescription: null,
          completionMode: 'submission_review',
          presentationMode: 'normal',
          preferredTemplatePolicy: 'auto',
          preferredTemplateId: null,
          targetCompanyId: null,
          repeatOfAssignmentId: null,
        }
        const item = {
          title: input.title,
          // No rounded display value travels with a related problem (only
          // the server's own full-precision `eloRating` does) — passing it
          // for both fields is exactly the fallback `runCatalogStage`
          // already applies when the two agree.
          eloRating: input.eloRating,
          elo: input.eloRating,
          estimatedMinutes: input.estimatedMinutes,
        }
        if (input.target === 'tray') {
          return runCatalogStage(item, 'tray', variables)
        }
        // The modal picks a *position*; `StageDestination` takes an identity.
        // Converting here, once, is what lets the retry stored in
        // `runCatalogStage`'s catch survive the refresh that catch performs
        // (LG-457) — see that type's docstring.
        //
        // A pick that already resolves to nothing becomes `'picked-day-gone'`
        // rather than being refused here: `runCatalogStage` still stages the
        // card and then reports the day as gone, so the coach's add is not
        // thrown away over a day they can re-choose from the tray.
        const week = weeks[input.target.weekIdx] ?? null
        const day = week?.days[input.target.dayIdx] ?? null
        return runCatalogStage(
          item,
          week === null || day?.id === undefined
            ? 'picked-day-gone'
            : { weekId: week.id, dayId: day.id },
          variables,
        )
      },
      // "Swap in instead of" (LG-360 §8) — one atomic `replace` call, not a
      // client-side cancel→stage→place chain: see the server method's own
      // docstring for why chaining is the hazard this avoids.
      replaceWithRelatedProblem: (input) => {
        const card = findAssignmentCard(input.cardId)
        if (!card) {
          return Promise.resolve({
            error: 'This card is no longer on the board.',
          })
        }
        // Same pre-check `stageRelatedProblem` makes, and the same message:
        // swapping in a problem already active elsewhere in the plan would
        // otherwise hit `CoachingAssignment_engagement_learningItem_active_uidx`
        // server-side and surface as an opaque error — two sibling writes for
        // the same coach mistake should read the same, not just one of them.
        //
        // Exempts the outgoing card's *own* learning item: `replace` cancels
        // it in the same transaction as creating the incoming one, so
        // swapping a card for its own problem would not actually trip that
        // constraint server-side (the outgoing row is already `cancelled`,
        // freeing the slot, before the new row is created) — refusing it
        // here would be a false "already in this plan" for what the server
        // would accept as a legitimate, if unusual, no-op-ish redo.
        if (
          input.learningItemId !== card.learningItemId &&
          activeLearningItemIds.has(input.learningItemId)
        ) {
          return Promise.resolve({
            error: `"${input.title}" is already in this plan. Complete or cancel the existing assignment before adding it again.`,
          })
        }
        const week = weeks[input.weekIdx] ?? null
        const day = week?.days[input.dayIdx] ?? null
        // Explicit `!== undefined` checks, not truthiness — `week.version`
        // is a number, and the same fix round closed the identical
        // truthiness gap in `runCatalogStage` above for the same reason.
        if (
          week === null ||
          week.version === undefined ||
          day === null ||
          day.id === undefined
        ) {
          return Promise.resolve({
            error:
              'That day is no longer available. Close and reopen the card to see the current board.',
          })
        }
        // Captured into their own bindings rather than read off `week`/`day`
        // inside the closure below: narrowing a destructured/property access
        // does not reliably survive into a callback passed to another
        // function, only a plain `const` does.
        const destinationDayId = day.id
        const destinationWeekId = week.id
        const destinationWeekVersion = week.version
        return detailWrite(
          () =>
            api.replace({
              // `assignmentIdentity`, not `cardIdentity`: the latter's
              // planDayId/weekId/weekVersion describe the outgoing card's
              // *current* slot, not the destination for the incoming one.
              ...assignmentIdentity(card),
              creationKey: randomUuid(),
              learningItemId: input.learningItemId,
              planDayId: destinationDayId,
              weekId: destinationWeekId,
              weekVersion: destinationWeekVersion,
              commandId: randomUuid(),
            }),
          'This card changed in another session. Close and reopen it to see its current state.',
        )
      },
      dayUsedHours: (weekIdx, dayIdx) =>
        weeks[weekIdx]?.days[dayIdx]?.cards.reduce(
          (total, card) =>
            total + (card.fixed || card.status === 'cancelled' ? 0 : card.est),
          0,
        ) ?? 0,
    }),
    [
      activeLearningItemIds,
      addCustom,
      addWeekReady,
      weekShapePending,
      api,
      bulkActionsReady,
      bulkActionsReason,
      distributeInput,
      assignmentIdentity,
      cardIdentity,
      cardDetails,
      cardDetailsPending,
      cardDetailsError,
      cardReview,
      cascadeContainer,
      clientId,
      detailWrite,
      companyTags,
      coverage.plan,
      coverage.tray,
      engagementId,
      filters,
      findAssignmentCard,
      flash,
      hiddenCols,
      columnOrder,
      items,
      libraryTab,
      mutation,
      optimizeInput,
      pacePending,
      placing,
      placingId,
      plan,
      removingIds,
      runCatalogStage,
      seenByProblemId,
      seenFilter,
      serverState.weeks,
      sorts,
      sourceTreeOpen,
      stageItem,
      stagePending,
      stageRetry,
      startStageOperation,
      tray,
      typeFilter,
      weekFolds,
      weeks,
    ],
  )
}

function PersistedCurriculumActive(props: {
  api: CurriculumPersistenceApi
  clientId: string
  children: ReactNode
}): ReactNode {
  const curriculum = usePersistedCurriculumSlice(props.api, props.clientId)
  return (
    <CoachingCurriculumProvider curriculum={curriculum}>
      {props.children}
    </CoachingCurriculumProvider>
  )
}

export function PersistedCurriculumBoundary(props: {
  clientId: string
  useApi?: UseCurriculumPersistenceApi
  children: ReactNode
}): ReactNode {
  const useApi = props.useApi ?? useTrpcCurriculumPersistenceApi
  const api = useApi(props.clientId)

  // A browse error is only fatal when it left nothing to show. The corpus
  // drains page by page, and React Query reports `error` for a failure on any
  // one of them — including page 5 of 7, with four pages of rows already in
  // `data`. Blanking the tab over that would throw away a usable library; the
  // slice's `browseIncomplete` flag is how the shortfall gets told instead.
  const browseFatal = Boolean(api.browse.error) && !api.browse.data
  if (api.plan.error || browseFatal || api.calendar.error) {
    return (
      <p role="alert" className="text-sm text-(--cd-red)">
        Persisted curriculum could not be loaded.
      </p>
    )
  }

  if (
    api.plan.isLoading ||
    api.browse.isLoading ||
    api.calendar.isLoading ||
    !api.plan.data ||
    !api.browse.data ||
    !api.calendar.data
  ) {
    return (
      <p role="status" className="text-sm text-(--cd-text-muted)">
        Loading persisted curriculum…
      </p>
    )
  }

  return (
    // Keyed on the engagement: every piece of board state the hook holds is
    // about one engagement's plan — which weeks the coach opened, which ones
    // they folded (and the storage key those are written under), what is
    // being placed. Switching client under one mount would carry all of it
    // across, so the switch is a remount instead.
    <PersistedCurriculumActive
      key={api.plan.data.engagement.id}
      api={api}
      clientId={props.clientId}
    >
      {props.children}
    </PersistedCurriculumActive>
  )
}
